import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FullCalendarComponent } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import moment from 'moment-timezone';
import { AppServiceManager } from '../../../../common/services/appServiceManager';
import { AuthService } from '../../../../common/services/auth.service';
import {
  applyTaxSettingsToRow,
  calculateSplitGrandTotal,
  calculateSplitRowTotal,
  convertNetSplitsToGross,
  createEmptySplitRow,
  getSplitAllocationTarget,
  getSplitGroupFees,
  getSplitPartLabel,
  buildSplitSrbInvoiceLines,
  buildSplitFbrInvoiceLines,
  isSplitAllocationValid,
  needsReceiptUpload,
  netSplitPayToGross,
  PaymentSplitRow,
  prepareFeeInvoiceForPrint,
  proportionalLineDiscount,
  recalculateRowTax,
  sumSplitIncludedTax,
  sumSplitPays,
  sumSplitTax,
} from '../../../../common/services/split-payment.util';
import { LanguageService } from '../../../LanguageService/Language.Service';
import { Configuration } from '../../../../config/config';
import { SingleReceiptPrintComponent } from '../../../shared/receipt/single-receipt-print/single-receipt-print.component';
import { IFeeDetailPrint } from '../../../shared/Domains/feeDetailPrint';
import { IBranch } from '../../../shared/Domains/branch';
import { ICompany } from '../../../shared/Domains/company';

const TIMEZONE_PKT = 'Asia/Karachi';

interface TrainerOption {
  label: string;
  value: string;
  color: string;
}

interface TrainerPackageOption {
  _id: string;
  name: string;
  type?: 'one_to_one' | 'class';
  classKind?: 'program' | 'single';
  monthWise?: boolean;
  isActive: boolean;
  trainerId?: string;
  price?: number;
  serviceIds?: string[];
}

interface SeriesSummary {
  startLocalDate: string;
  endLocalDate: string;
  weekdays: number[];
  sessionCount: number;
}

interface DaySeriesCard {
  seriesId: string;
  sampleSession: TrainerSessionRow;
  sessions: TrainerSessionRow[];
  trainerId: string;
  packageName: string;
  price: number;
  minSeats: number;
  monthWise?: boolean;
  summary?: SeriesSummary;
}

interface SeriesEnrollmentGroup {
  seriesId?: string;
  feeId?: string;
  packageName: string;
  trainerId: string;
  bookings: TrainerBookingRow[];
}

interface EnrollMonthOption {
  key: string;
  label: string;
  sessionCount: number;
  minSeats: number;
}

interface TrainerBookingFeeRow {
  _id: string;
  bookingId?: string;
  sessionId?: string;
  packageId: string;
  seriesId?: string;
  trainerId: string;
  customerId: string;
  baseAmount: number;
  discount: number;
  tax: number;
  taxRate: number;
  taxCalculationType: string;
  totalPaid: number;
  paymentMode: string;
  invoice?: string;
  description?: string;
  feeReceiptImageUrl?: string;
  createdAt?: string;
  balance?: number;
  parentFeeId?: string;
  splitGroupId?: string;
  splitIndex?: number;
  splitTotal?: number;
  isSplitPayment?: boolean;
  SRBInvoiceID?: string;
  FBRInvoiceID?: string;
}

interface TrainerSessionRow {
  _id: string;
  type?: 'one_to_one' | 'class';
  packageId?: string;
  seriesId?: string;
  seriesSummary?: SeriesSummary;
  trainerId?: string;
  localDate: string;
  startMinutes: number;
  durationMinutes: number;
  maxMembers: number;
  bookedCount: number;
  status: 'scheduled' | 'cancelled';
}

interface TrainerBookingRow {
  _id: string;
  sessionId: string;
  packageId: string;
  trainerId: string;
  seriesId?: string;
  status: string;
  feeId?: string;
  createdAt?: string;
}

const TRAINER_COLORS = [
  '#1d6fd8',
  '#1f8a5b',
  '#7c3aed',
  '#db2777',
  '#ea580c',
  '#0f766e',
  '#2563eb',
  '#4b5563',
  '#b45309',
  '#be123c',
];

@Component({
  selector: 'app-book-trainer',
  templateUrl: './book-trainer.component.html',
  styleUrls: ['./book-trainer.component.css'],
})
export class BookTrainerComponent implements OnChanges, OnInit, OnDestroy {
  @Input() memberId = '';
  @Input() branchId = '';
  @Input() memberName = '';
  @Input() memberTag = '';
  @ViewChild('calendar') calendarComponent!: FullCalendarComponent;
  @ViewChild(SingleReceiptPrintComponent)
  singleReceiptPrint!: SingleReceiptPrintComponent;

  loading = false;
  /** Delayed overlay when calendar refresh takes longer than ~2s. */
  calendarBusy = false;
  /** First fetch of trainers + packages after tab mount. */
  bootstrapLoading = false;
  bookingBusy = false;
  cancellingId: string | null = null;

  trainerOptions: TrainerOption[] = [];
  /** Google Calendar–style filters */
  trainingServiceOptions: { label: string; value: string }[] = [];
  selectedServiceIds = new Set<string>();
  servicesSectionOpen = true;
  /** Phone/tablet: filters sit above the day slots and start collapsed. */
  isNarrow = false;
  mobileFiltersOpen = false;
  filterOneToOne = false;
  filterClass = false;
  filterSeries = false;
  checkedTrainerIds = new Set<string>();
  typesSectionOpen = true;
  trainersSectionOpen = true;

  packagesByTrainerId = new Map<string, TrainerPackageOption[]>();
  sessionsByTrainerId = new Map<string, TrainerSessionRow[]>();
  /** Cached range key for sessionsByTrainerId entries */
  private sessionsCacheRange = '';

  packageById = new Map<string, TrainerPackageOption>();
  sessions: TrainerSessionRow[] = [];
  availableSessions: TrainerSessionRow[] = [];
  memberBookings: TrainerBookingRow[] = [];
  sessionById = new Map<string, TrainerSessionRow>();

  events: any[] = [];
  calendarOptions: any = {};
  calendarTitle = '';
  selectedLocalDate: string | null = null;
  /** Cached day panel rows — rebuilt with availability, not on every CD. */
  daySeriesCardsList: DaySeriesCard[] = [];
  daySingleSessionsList: TrainerSessionRow[] = [];

  /** 0 = Book Session, 1 = All Bookings, 2 = Fee History */
  activeTabIndex = 0;
  allBookingsLoading = false;
  allMemberBookings: TrainerBookingRow[] = [];
  allSessionById = new Map<string, TrainerSessionRow>();
  allPackageById = new Map<string, TrainerPackageOption>();
  detailBooking: TrainerBookingRow | null = null;
  detailSeriesGroup: SeriesEnrollmentGroup | null = null;
  seriesGroups: SeriesEnrollmentGroup[] = [];
  sharedFeeGroups: SeriesEnrollmentGroup[] = [];
  private sharedFeeBookingIdSet = new Set<string>();

  feeHistoryLoading = false;
  /** Full list from API; UI date-filters + paginates from this. */
  feeHistoryAllRows: TrainerBookingFeeRow[] = [];
  feeHistoryFirst = 0;
  readonly feeHistoryPageSize = 5;
  feeHistoryFrom: Date | null = null;
  feeHistoryTo: Date | null = null;

  /** Book dialog: package + fee (class and 1:1). */
  bookDialogVisible = false;
  bookTarget: TrainerSessionRow | null = null;
  bookPackageId: string | null = null;
  oneToOnePackageOptions: { label: string; value: string }[] = [];
  bookClassPackageLabel = '';
  sameSlotSessionOptions: { label: string; value: string }[] = [];
  selectedBookSessionIds: string[] = [];
  sameSlotPanelOpen = false;
  bookBaseAmount = 0;
  /** Editable fee collected now; null while user clears the field. */
  bookPayNow: number | null = 0;
  private lastBookBaseForPayNow = 0;
  bookDiscount: number | null = 0;
  bookPaymentMode: string | null = null;
  bookTax = 0;
  bookTotalPaid = 0;
  bookAmountDueTotal = 0;
  bookThisPaymentTotal = 0;
  bookBalanceRemaining = 0;
  bookTaxType = 'NO_TAX';
  bookTaxRate = 0;
  bookDiscountError = false;
  bookPayNowError = false;
  multiplePaymentModesEnabled = false;
  paymentSplits: PaymentSplitRow[] = [];
  payRemainingDialogVisible = false;
  payRemainingTarget: TrainerBookingFeeRow | null = null;
  payRemainingPayNow = 0;
  payRemainingPayNowError = false;
  payRemainingPaymentMode: string | null = null;
  payRemainingBusy = false;
  feeById = new Map<string, TrainerBookingFeeRow>();
  paymentOptions: { label: string; value: string }[] = [];
  isDiscountFormulaReverse = false;
  hideTaxInfo = false;
  selectedReceiptFile: File | null = null;
  selectedReceiptName = '';
  feeReceiptImageUrl = '';
  receiptUploading = false;
  bookComment = '';
  /** Book dialog is shared: 'session' = 1:1 / single class, 'series' = program enrollment. */
  bookMode: 'session' | 'series' = 'session';
  enrollTarget: DaySeriesCard | null = null;
  enrolledSeriesIds = new Set<string>();
  fullyEnrolledSeriesIds = new Set<string>();
  enrollTotalProgramMonths = 1;
  enrollMonthOptions: EnrollMonthOption[] = [];
  selectedEnrollMonths: string[] = [];
  enrollMonthPanelOpen = false;
  enrollMonthWise = false;
  deletingFeeId: string | null = null;
  commentDialogVisible = false;
  commentDialogText = '';

  printBranch: IBranch | null = null;
  printCompany: ICompany | null = null;
  invoiceMessage = '';

  private branchPayments: any[] = [];
  /** Role names allowed to delete trainer fees (from branch.permissions.trainerModule). */
  private feeDeleteRoles: string[] = [];
  private rangeFrom = '';
  private rangeTo = '';
  private bookedSessionIds = new Set<string>();
  /** Debounce rapid service/type/trainer filter toggles. */
  private filterDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  /** Show calendarBusy only if refresh exceeds this delay. */
  private calendarBusyTimer: ReturnType<typeof setTimeout> | null = null;
  private calendarWorkGen = 0;
  private static readonly FILTER_DEBOUNCE_MS = 120;
  private static readonly CALENDAR_BUSY_DELAY_MS = 2000;
  private readonly narrowQuery = '(max-width: 900px)';
  private narrowMql: MediaQueryList | null = null;
  private readonly onNarrowChange = (ev: MediaQueryListEvent): void => {
    this.isNarrow = ev.matches;
    if (this.isNarrow) {
      this.mobileFiltersOpen = false;
      this.focusNarrowDay(new Date());
    }
    this.cdr.markForCheck();
  };
  config = new Configuration();

  constructor(
    private LanguageService: LanguageService,
    private appServiceManager: AppServiceManager,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initCalendar();
    this.resetFeeHistoryDateFilter();
    this.isNarrow = this.readNarrow();
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    this.narrowMql = window.matchMedia(this.narrowQuery);
    this.isNarrow = this.narrowMql.matches;
    this.narrowMql.addEventListener('change', this.onNarrowChange);
    if (this.isNarrow) {
      queueMicrotask(() => this.focusNarrowDay(new Date()));
    }
  }

  ngOnDestroy(): void {
    this.narrowMql?.removeEventListener('change', this.onNarrowChange);
    this.clearFilterDebounce();
    this.endCalendarWork();
  }

  toggleMobileFilters(): void {
    this.mobileFiltersOpen = !this.mobileFiltersOpen;
  }

  setLabel(label: string) {
    return this.LanguageService.GetCurrentLanguage(
      'BookTrainerComponent',
      label,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.memberId || changes.branchId) &&
      this.memberId &&
      this.branchId
    ) {
      this.resetFilterState();
      this.bootstrapLoading = true;
      this.loadBranchPayments();
      this.loadTrainersAndPackages();
      if (this.activeTabIndex === 1) {
        this.loadAllMemberBookings();
      } else if (this.activeTabIndex === 2) {
        this.loadFeeHistory();
      } else {
        this.allMemberBookings = [];
        this.detailBooking = null;
        this.detailSeriesGroup = null;
        this.seriesGroups = [];
        this.sharedFeeGroups = [];
        this.sharedFeeBookingIdSet = new Set();
        this.feeHistoryAllRows = [];
        this.feeHistoryFirst = 0;
        this.resetFeeHistoryDateFilter();
      }
    }
  }

  onTabChange(ev: { index?: number }): void {
    const idx = typeof ev?.index === 'number' ? ev.index : this.activeTabIndex;
    this.activeTabIndex = idx;
    if (idx === 1) {
      this.loadAllMemberBookings();
    } else if (idx === 2) {
      this.detailBooking = null;
      this.detailSeriesGroup = null;
      this.loadFeeHistory();
    } else {
      this.detailBooking = null;
      this.detailSeriesGroup = null;
    }
  }

  needsBookReceipt(): boolean {
    if (this.multiplePaymentModesEnabled) {
      return (this.paymentSplits || []).some((row) =>
        needsReceiptUpload(row.paymentMode),
      );
    }
    return needsReceiptUpload(this.bookPaymentMode || undefined);
  }

  needsPayRemainingReceipt(): boolean {
    if (this.multiplePaymentModesEnabled) {
      return (this.paymentSplits || []).some((row) =>
        needsReceiptUpload(row.paymentMode),
      );
    }
    return needsReceiptUpload(this.payRemainingPaymentMode || undefined);
  }

  get isPayRemainingDialog(): boolean {
    return this.payRemainingDialogVisible;
  }

  get activePayNow(): number {
    return this.isPayRemainingDialog
      ? Number(this.payRemainingPayNow) || 0
      : Number(this.bookPayNow) || 0;
  }

  get activePayMax(): number {
    return this.isPayRemainingDialog
      ? Number(this.payRemainingTarget?.balance) || 0
      : Number(this.bookBaseAmount) || 0;
  }

  get activeDiscount(): number {
    return this.isPayRemainingDialog ? 0 : Number(this.bookDiscount) || 0;
  }

  get activePaymentMode(): string | null {
    return this.isPayRemainingDialog
      ? this.payRemainingPaymentMode
      : this.bookPaymentMode;
  }

  get splitAllocationTarget(): number {
    return getSplitAllocationTarget(this.activePayNow, this.activeDiscount);
  }

  get allocatedSplitTotal(): number {
    return sumSplitPays(this.paymentSplits);
  }

  get splitTotalTax(): number {
    return sumSplitTax(this.paymentSplits);
  }

  get splitTotalIncludedTax(): number {
    return sumSplitIncludedTax(this.paymentSplits);
  }

  get splitGrandTotal(): number {
    return calculateSplitGrandTotal(this.paymentSplits);
  }

  get splitValidationMessage(): string {
    if (!this.multiplePaymentModesEnabled) {
      return '';
    }
    if (this.hasIncompleteSplitRows()) {
      return this.setLabel('splitFillAllRowsErrorLabel');
    }
    if (this.hasDuplicateSplitPaymentModes()) {
      return this.setLabel('splitDuplicateModeErrorLabel');
    }
    if (!this.isSplitAllocationValid()) {
      return this.setLabel('splitAllocationMismatchErrorLabel');
    }
    return '';
  }

  get isSeriesBookMode(): boolean {
    return this.bookMode === 'series';
  }

  /** 1:1 blue / class green / program violet — same as schedule + package chips. */
  get bookTone(): 'one' | 'class' | 'series' {
    if (this.isSeriesBookMode) {
      return 'series';
    }
    return this.bookTarget && this.isOneToOneSession(this.bookTarget)
      ? 'one'
      : 'class';
  }

  get bookDdPanelClass(): string {
    return `bt-clay-dd-panel bt-clay-dd-panel--${this.bookTone}`;
  }

  get bookDialogHeader(): string {
    return this.setLabel(
      this.isSeriesBookMode
        ? 'enrollConfirmHeaderLabel'
        : 'bookConfirmHeaderLabel',
    );
  }

  get bookConfirmLabel(): string {
    return this.setLabel(
      this.isSeriesBookMode ? 'enrollProgramBtnLabel' : 'bookBtnLabel',
    );
  }

  get bookConfirmDisabled(): boolean {
    if (this.bookingBusy || this.receiptUploading) {
      return true;
    }
    if (this.bookPayNowError || this.bookDiscountError) {
      return true;
    }
    if (this.multiplePaymentModesEnabled && !this.isSplitFormValid()) {
      return true;
    }
    if (!this.multiplePaymentModesEnabled && !this.bookPaymentMode) {
      return true;
    }
    if (this.isSeriesBookMode) {
      if (this.showEnrollMonthPicker && this.selectedEnrollMonths.length < 1) {
        return true;
      }
      return this.enrollDialogMinSeats <= 0;
    }
    if (!this.bookTarget) {
      return true;
    }
    if (this.isOneToOneSession(this.bookTarget) && !this.bookPackageId) {
      return true;
    }
    return this.selectedBookSessionIds.length < 1;
  }

  get payRemainingConfirmDisabled(): boolean {
    if (
      this.payRemainingBusy ||
      this.receiptUploading ||
      this.payRemainingPayNowError
    ) {
      return true;
    }
    if (this.multiplePaymentModesEnabled && !this.isSplitFormValid()) {
      return true;
    }
    return !this.multiplePaymentModesEnabled && !this.payRemainingPaymentMode;
  }

  get showSameSlotMultiSelect(): boolean {
    return !this.isSeriesBookMode && this.sameSlotSessionOptions.length > 1;
  }

  get sameSlotTriggerLabel(): string {
    const n = this.selectedBookSessionIds.length;
    if (n <= 1) {
      const id = this.selectedBookSessionIds[0];
      return (
        this.sameSlotSessionOptions.find((o) => o.value === id)?.label ||
        this.setLabel('sameSlotSelectPlaceholderLabel')
      );
    }
    return this.setLabel('sameSlotSelectedCountLabel').replace(
      '{count}',
      '' + n,
    );
  }

  toggleSameSlotPanel(ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.bookingBusy) {
      return;
    }
    this.sameSlotPanelOpen = !this.sameSlotPanelOpen;
  }

  @HostListener('document:click', ['$event'])
  onSameSlotDocumentClick(ev: MouseEvent): void {
    const el = ev.target as HTMLElement | null;
    if (this.sameSlotPanelOpen && !el?.closest?.('.bt-same-slot-ms-wrap')) {
      this.sameSlotPanelOpen = false;
    }
    if (
      this.enrollMonthPanelOpen &&
      !el?.closest?.('.bt-enroll-month-ms-wrap')
    ) {
      this.enrollMonthPanelOpen = false;
    }
  }

  isSameSlotSeed(id: string): boolean {
    return !!id && id === this.bookTarget?._id;
  }

  isSameSlotSelected(id: string): boolean {
    return this.selectedBookSessionIds.includes(id);
  }

  get allSameSlotsSelected(): boolean {
    return (
      !!this.sameSlotSessionOptions.length &&
      this.sameSlotSessionOptions.every((o) =>
        this.selectedBookSessionIds.includes(o.value),
      )
    );
  }

  toggleAllSameSlots(): void {
    if (this.bookingBusy) {
      return;
    }
    const seed = this.bookTarget?._id;
    this.selectedBookSessionIds = this.allSameSlotsSelected
      ? seed
        ? [seed]
        : []
      : this.sameSlotSessionOptions.map((o) => o.value);
    this.onSameSlotSelectionChange();
  }

  onSameSlotRowClick(id: string): void {
    if (this.bookingBusy || this.isSameSlotSeed(id)) {
      return;
    }
    if (this.isSameSlotSelected(id)) {
      this.selectedBookSessionIds = this.selectedBookSessionIds.filter(
        (x) => x !== id,
      );
    } else if (!this.selectedBookSessionIds.includes(id)) {
      this.selectedBookSessionIds.push(id);
    }
    this.onSameSlotSelectionChange();
  }

  onSameSlotSelectionChange(): void {
    const seed = this.bookTarget?._id;
    if (seed && !this.selectedBookSessionIds.includes(seed)) {
      this.selectedBookSessionIds.push(seed);
    }
    this.applyBookPackagePrice(true);
    this.recalcBookFee();
  }

  confirmBookDialog(): void {
    if (this.isSeriesBookMode) {
      this.confirmEnroll();
      return;
    }
    this.confirmBook();
  }

  get hasCheckedTrainers(): boolean {
    return this.checkedTrainerIds.size > 0;
  }

  get anyPackageTypeOn(): boolean {
    return this.filterOneToOne || this.filterClass || this.filterSeries;
  }

  get visibleTrainers(): TrainerOption[] {
    if (!this.selectedServiceIds.size || !this.anyPackageTypeOn) {
      return [];
    }
    return this.trainerOptions.filter((t) => this.trainerMatchesTypeFilter(t.value));
  }

  isServiceChecked(id: string): boolean {
    return this.selectedServiceIds.has(String(id));
  }

  toggleServicesSection(): void {
    this.servicesSectionOpen = !this.servicesSectionOpen;
  }

  toggleService(id: string): void {
    const key = String(id);
    if (this.selectedServiceIds.has(key)) {
      this.selectedServiceIds.delete(key);
    } else {
      this.selectedServiceIds.add(key);
    }
    this.selectedServiceIds = new Set(this.selectedServiceIds);
    this.onServiceFilterChange();
  }

  isTrainerChecked(id: string): boolean {
    return this.checkedTrainerIds.has(String(id));
  }

  toggleTypesSection(): void {
    this.typesSectionOpen = !this.typesSectionOpen;
  }

  toggleTrainersSection(): void {
    this.trainersSectionOpen = !this.trainersSectionOpen;
  }

  toggleFilterOneToOne(): void {
    this.filterOneToOne = !this.filterOneToOne;
    this.onTypeFilterChange();
  }

  toggleFilterClass(): void {
    this.filterClass = !this.filterClass;
    this.onTypeFilterChange();
  }

  toggleFilterSeries(): void {
    this.filterSeries = !this.filterSeries;
    this.onTypeFilterChange();
  }

  toggleTrainer(id: string): void {
    const key = String(id);
    if (this.checkedTrainerIds.has(key)) {
      this.checkedTrainerIds.delete(key);
      this.checkedTrainerIds = new Set(this.checkedTrainerIds);
      this.selectedLocalDate = null;
      this.scheduleCalendarRefresh();
      return;
    }
    this.checkedTrainerIds.add(key);
    this.checkedTrainerIds = new Set(this.checkedTrainerIds);
    this.ensureSessionsForChecked().then(() => {
      this.loadMemberBookingsOnce();
    });
  }

  get selectedDateLabel(): string {
    if (!this.selectedLocalDate) {
      return '';
    }
    const [y, m, d] = this.selectedLocalDate.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  sessionsForSelectedDay(): TrainerSessionRow[] {
    if (!this.selectedLocalDate) {
      return [];
    }
    return this.availableSessions.filter(
      (s) => s.localDate === this.selectedLocalDate,
    );
  }

  private rebuildDayPanelLists(): void {
    if (!this.selectedLocalDate) {
      this.daySeriesCardsList = [];
      this.daySingleSessionsList = [];
      return;
    }
    const daySessions = this.sessionsForSelectedDay();
    this.daySingleSessionsList = daySessions.filter((s) => !s.seriesId);

    const bySeries = new Map<string, TrainerSessionRow[]>();
    for (const s of daySessions) {
      const sid = s.seriesId ? String(s.seriesId) : '';
      if (!sid) {
        continue;
      }
      const list = bySeries.get(sid) || [];
      list.push(s);
      bySeries.set(sid, list);
    }
    const cards: DaySeriesCard[] = [];
    bySeries.forEach((sessions, seriesId) => {
      const sample = sessions[0];
      if (!sample) {
        return;
      }
      const enrollSessions = this.futureSeriesSessions(seriesId);
      const pkg = this.findPackageForSession(sample);
      const minSeats = this.minSeatsForSessions(
        enrollSessions.length ? enrollSessions : sessions,
      );
      cards.push({
        seriesId,
        sampleSession: sample,
        sessions: enrollSessions.length ? enrollSessions : sessions,
        trainerId: String(sample.trainerId || ''),
        packageName: pkg?.name || this.sessionPackageName(sample),
        price: Number(pkg?.price) || 0,
        minSeats,
        monthWise: pkg?.monthWise === true,
        summary: sample.seriesSummary,
      });
    });
    this.daySeriesCardsList = cards.sort(
      (a, b) => a.sampleSession.startMinutes - b.sampleSession.startMinutes,
    );
  }

  trackBySeriesId(_index: number, card: DaySeriesCard): string {
    return card.seriesId;
  }

  private futureSeriesSessions(seriesId: string): TrainerSessionRow[] {
    return this.sessions
      .filter(
        (s) =>
          String(s.seriesId) === String(seriesId) &&
          s.status === 'scheduled' &&
          this.isSessionStartInFuture(s.localDate, s.startMinutes),
      )
      .sort(
        (a, b) =>
          a.localDate.localeCompare(b.localDate) || a.startMinutes - b.startMinutes,
      );
  }

  private minSeatsForSessions(sessions: TrainerSessionRow[]): number {
    if (!sessions.length) {
      return 0;
    }
    const min = sessions.reduce(
      (acc, sess) =>
        Math.min(acc, Math.max(0, sess.maxMembers - sess.bookedCount)),
      Infinity,
    );
    return Number.isFinite(min) ? min : 0;
  }

  isEnrolledInSeries(seriesId: string): boolean {
    return this.enrolledSeriesIds.has(String(seriesId));
  }

  isFullyEnrolledInSeries(seriesId: string): boolean {
    return this.fullyEnrolledSeriesIds.has(String(seriesId));
  }

  get showEnrollMonthPicker(): boolean {
    return (
      this.enrollMonthWise &&
      this.enrollTotalProgramMonths > 1 &&
      this.enrollMonthOptions.length > 0
    );
  }

  get enrollDialogSessions(): TrainerSessionRow[] {
    const all = this.enrollTarget?.sessions || [];
    if (!this.showEnrollMonthPicker) {
      return all;
    }
    const selected = new Set(this.selectedEnrollMonths);
    return all.filter((s) => selected.has(String(s.localDate || '').slice(0, 7)));
  }

  get enrollDialogMinSeats(): number {
    return this.minSeatsForSessions(this.enrollDialogSessions);
  }

  get enrollPriceHint(): string {
    if (!this.showEnrollMonthPicker) {
      return this.setLabel('seriesProgramPriceHintLabel');
    }
    return this.setLabel('programMonthDueHintLabel')
      .replace('{n}', String(this.selectedEnrollMonths.length))
      .replace('{price}', String(Number(this.enrollTarget?.price) || 0));
  }

  seriesScheduleLine(card: DaySeriesCard): string {
    return [this.seriesWeekdaysLine(card), this.seriesSlotTime(card), this.seriesDateRange(card)]
      .filter((p) => p && p !== '—')
      .join(' · ');
  }

  seriesWeekdaysLine(card: DaySeriesCard): string {
    const days = card.summary?.weekdays;
    if (!days?.length) {
      return '—';
    }
    return days
      .map((d) => this.weekdayShort(d))
      .filter(Boolean)
      .join(', ') || '—';
  }

  seriesSlotTime(card: DaySeriesCard): string {
    return this.sessionWindow(card.sampleSession) || '—';
  }

  seriesDateRange(card: DaySeriesCard): string {
    const summary = card.summary;
    if (summary?.startLocalDate && summary?.endLocalDate) {
      return `${summary.startLocalDate} → ${summary.endLocalDate}`;
    }
    return '—';
  }

  private weekdayShort(d: number): string {
    const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return labels[d] || '';
  }

  private rebuildSeriesGroups(): void {
    const byKey = new Map<string, SeriesEnrollmentGroup>();
    for (const b of this.classBookings()) {
      const sid = b.seriesId ? String(b.seriesId) : '';
      if (!sid) {
        continue;
      }
      const key = `${sid}|${b.feeId || ''}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.bookings.push(b);
      } else {
        byKey.set(key, {
          seriesId: sid,
          feeId: b.feeId,
          packageName: this.allBookingPackageName(b),
          trainerId: String(b.trainerId),
          bookings: [b],
        });
      }
    }
    const groups = Array.from(byKey.values()).sort((a, b) =>
      String(b.bookings[0]?.createdAt || '').localeCompare(
        String(a.bookings[0]?.createdAt || ''),
      ),
    );
    for (const g of groups) {
      this.sortGroupBookings(g.bookings);
    }
    this.seriesGroups = groups;
    this.rebuildSharedFeeGroups();
  }

  private rebuildSharedFeeGroups(): void {
    const byFee = new Map<string, TrainerBookingRow[]>();
    for (const b of this.allMemberBookings) {
      if (b.seriesId || !b.feeId) {
        continue;
      }
      const fid = String(b.feeId);
      const list = byFee.get(fid);
      if (list) {
        list.push(b);
      } else {
        byFee.set(fid, [b]);
      }
    }
    const groups: SeriesEnrollmentGroup[] = [];
    const ids = new Set<string>();
    byFee.forEach((bookings, feeId) => {
      if (bookings.length < 2) {
        return;
      }
      this.sortGroupBookings(bookings);
      for (const b of bookings) {
        ids.add(b._id);
      }
      groups.push({
        feeId,
        packageName: this.allBookingPackageName(bookings[0]),
        trainerId: String(bookings[0].trainerId),
        bookings,
      });
    });
    groups.sort((a, b) =>
      String(b.bookings[0]?.createdAt || '').localeCompare(
        String(a.bookings[0]?.createdAt || ''),
      ),
    );
    this.sharedFeeGroups = groups;
    this.sharedFeeBookingIdSet = ids;
  }

  private sortGroupBookings(bookings: TrainerBookingRow[]): void {
    bookings.sort((a, b) => {
      const dateCmp = this.allBookingDate(a).localeCompare(this.allBookingDate(b));
      if (dateCmp) {
        return dateCmp;
      }
      const sa = this.allSessionById.get(String(a.sessionId));
      const sb = this.allSessionById.get(String(b.sessionId));
      return (sa?.startMinutes || 0) - (sb?.startMinutes || 0);
    });
  }

  trackBySeriesGroup(_index: number, g: SeriesEnrollmentGroup): string {
    return `${g.seriesId}|${g.feeId || ''}`;
  }

  singleClassBookings(): TrainerBookingRow[] {
    return this.classBookings().filter(
      (b) => !b.seriesId && !this.sharedFeeBookingIdSet.has(b._id),
    );
  }

  closeDay(): void {
    this.selectedLocalDate = null;
    this.rebuildDayPanelLists();
  }

  goToday(): void {
    if (this.isNarrow) {
      this.focusNarrowDay(new Date());
      return;
    }
    this.calendarComponent?.getApi()?.today();
  }

  prevMonth(): void {
    if (this.isNarrow) {
      this.shiftNarrowDay(-1);
      return;
    }
    this.calendarComponent?.getApi()?.prev();
  }

  nextMonth(): void {
    if (this.isNarrow) {
      this.shiftNarrowDay(1);
      return;
    }
    this.calendarComponent?.getApi()?.next();
  }

  private readNarrow(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(this.narrowQuery).matches;
  }

  /** Day list for the phone layout. Calendar stays mounted so the month still loads. */
  private focusNarrowDay(date: Date): void {
    this.calendarComponent?.getApi()?.gotoDate(date);
    this.openDay(this.toLocalDateStr(date));
  }

  private shiftNarrowDay(delta: number): void {
    const base = this.selectedLocalDate
      ? this.parseLocalDate(this.selectedLocalDate)
      : new Date();
    base.setDate(base.getDate() + delta);
    this.focusNarrowDay(base);
  }

  private parseLocalDate(value: string): Date {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }

  sessionTitle(s: TrainerSessionRow): string {
    const name = this.sessionPackageName(s);
    const seats = `${s.bookedCount}/${s.maxMembers}`;
    return `${this.sessionTypeLabel(s)} · ${name} · ${this.sessionWindow(s)} · ${seats}`;
  }

  sessionWindow(s: TrainerSessionRow): string {
    return `${this.hhmm(s.startMinutes)}–${this.hhmm(
      s.startMinutes + s.durationMinutes,
    )}`;
  }

  sessionDisplayName(s: TrainerSessionRow): string {
    return this.sessionTypeLabel(s);
  }

  sessionTypeLabel(s: TrainerSessionRow): string {
    return this.isOneToOneSession(s)
      ? this.setLabel('oneToOneSessionLabel')
      : this.setLabel('classGroupLabel');
  }

  sessionPackageName(s: TrainerSessionRow): string {
    const pkg = this.findPackageForSession(s);
    if (pkg?.name) {
      return pkg.name;
    }
    if (this.isOneToOneSession(s)) {
      // 1:1 sessions have no packageId (picked at book). Show trainer 1:1 pkg names
      // including inactive — inactive still labels the row; book dialog stays active-only.
      const names = this.oneToOnePackageNamesForDisplay(s.trainerId);
      return names.length ? names.join(' · ') : '—';
    }
    return this.setLabel('sessionFallbackTitleLabel');
  }

  /** Day-panel labels: active first, then inactive — so unpublished pkgs still show a name. */
  private oneToOnePackageNamesForDisplay(trainerId: unknown): string[] {
    const pkgs = this.trainerPackages(trainerId).filter(
      (p) => p.type === 'one_to_one',
    );
    const active = pkgs.filter((p) => p.isActive !== false);
    const use = active.length ? active : pkgs;
    return use
      .map((p) => (p.name || '').trim())
      .filter(Boolean);
  }

  /** Book dialog: active 1:1 packages overlapping selected services. */
  private trainerOneToOnePackages(trainerId: unknown): TrainerPackageOption[] {
    return this.trainerPackages(trainerId).filter(
      (p) =>
        p.isActive !== false &&
        p.type === 'one_to_one' &&
        this.packageOverlapsSelectedServices(p),
    );
  }

  private trainerPackages(trainerId: unknown): TrainerPackageOption[] {
    const id = this.refId(trainerId);
    if (!id) {
      return [];
    }
    const direct = this.packagesByTrainerId.get(id);
    if (direct?.length) {
      return direct;
    }
    for (const [key, pkgs] of Array.from(this.packagesByTrainerId.entries())) {
      if (this.refId(key) === id && pkgs?.length) {
        return pkgs;
      }
    }
    return Array.from(this.packageById.values()).filter(
      (p) => this.refId(p.trainerId) === id,
    );
  }

  private findPackageForSession(
    s: TrainerSessionRow,
  ): TrainerPackageOption | undefined {
    const raw = s.packageId as unknown;
    if (raw && typeof raw === 'object' && (raw as { name?: string }).name) {
      const embedded = raw as TrainerPackageOption;
      return {
        _id: this.refId((embedded as { _id?: unknown })._id) || this.refId(raw),
        name: String(embedded.name),
        type: embedded.type,
        isActive: embedded.isActive !== false,
      };
    }
    const pid = this.refId(raw);
    if (!pid) {
      return undefined;
    }
    const hit = this.packageById.get(pid);
    if (hit) {
      return hit;
    }
    const trainerId = this.refId(s.trainerId);
    const trainerPkgs = this.packagesByTrainerId.get(trainerId) || [];
    return trainerPkgs.find((p) => this.refId(p._id) === pid);
  }

  /** Normalize Mongo id / populated ref to a stable string key. */
  private refId(value: unknown): string {
    if (value == null || value === '') {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return String(value);
    }
    if (typeof value === 'object') {
      const o = value as {
        _id?: unknown;
        id?: unknown;
        toHexString?: () => string;
      };
      if (typeof o.toHexString === 'function') {
        return o.toHexString();
      }
      if (o._id != null) {
        return this.refId(o._id);
      }
      if (o.id != null) {
        return this.refId(o.id);
      }
    }
    return String(value);
  }

  isOneToOneSession(s: TrainerSessionRow): boolean {
    if (s.type === 'one_to_one') {
      return true;
    }
    if (s.type === 'class') {
      return false;
    }
    return !s.packageId;
  }

  bookingTitle(b: TrainerBookingRow): string {
    const session =
      this.sessionById.get(String(b.sessionId)) ||
      this.findCachedSession(String(b.sessionId));
    const pkg = this.packageById.get(String(b.packageId));
    const name = pkg?.name || this.setLabel('sessionFallbackTitleLabel');
    if (session) {
      return `${name} · ${session.localDate} · ${this.hhmm(
        session.startMinutes,
      )}–${this.hhmm(session.startMinutes + session.durationMinutes)}`;
    }
    return name;
  }

  oneToOneBookings(): TrainerBookingRow[] {
    return this.allMemberBookings.filter(
      (b) =>
        this.bookingKind(b) === 'one_to_one' &&
        !this.sharedFeeBookingIdSet.has(b._id),
    );
  }

  oneToOneSharedGroups(): SeriesEnrollmentGroup[] {
    return this.sharedFeeGroups.filter(
      (g) => this.bookingKind(g.bookings[0]) === 'one_to_one',
    );
  }

  classSharedGroups(): SeriesEnrollmentGroup[] {
    return this.sharedFeeGroups.filter(
      (g) => this.bookingKind(g.bookings[0]) === 'class',
    );
  }

  oneToOneListCount(): number {
    return this.oneToOneBookings().length + this.oneToOneSharedGroups().length;
  }

  classListCount(): number {
    return this.singleClassBookings().length + this.classSharedGroups().length;
  }

  sharedGroupTone(
    g: SeriesEnrollmentGroup,
  ): 'one_to_one' | 'class' | 'series' {
    if (g.seriesId) {
      return 'series';
    }
    return this.bookingKind(g.bookings[0]);
  }

  sharedGroupBadgeLabel(g: SeriesEnrollmentGroup): string {
    if (g.seriesId) {
      return this.setLabel('seriesGroupBadgeLabel');
    }
    return this.bookingToneLabel(g.bookings[0]);
  }

  classBookings(): TrainerBookingRow[] {
    return this.allMemberBookings.filter(
      (b) => this.bookingKind(b) === 'class',
    );
  }

  bookingKind(b: TrainerBookingRow): 'one_to_one' | 'class' {
    const session = this.allSessionById.get(String(b.sessionId));
    if (session?.type === 'one_to_one' || session?.type === 'class') {
      return session.type;
    }
    const pkg = this.allPackageById.get(String(b.packageId));
    if (pkg?.type === 'one_to_one' || pkg?.type === 'class') {
      return pkg.type;
    }
    return session && !session.packageId ? 'one_to_one' : 'class';
  }

  bookingTone(b: TrainerBookingRow): 'one_to_one' | 'class' | 'series' {
    return b?.seriesId ? 'series' : this.bookingKind(b);
  }

  bookingToneLabel(b: TrainerBookingRow): string {
    const tone = this.bookingTone(b);
    if (tone === 'one_to_one') {
      return this.setLabel('oneToOneGroupLabel');
    }
    if (tone === 'series') {
      return this.setLabel('seriesGroupBadgeLabel');
    }
    return this.setLabel('classGroupLabel');
  }

  trainerName(trainerId?: string): string {
    const hit = this.trainerOptions.find((t) => t.value === String(trainerId));
    return hit?.label || this.setLabel('trainerFallbackLabel');
  }

  allBookingPackageName(b: TrainerBookingRow): string {
    const pkg = this.allPackageById.get(String(b.packageId));
    if (pkg?.name) {
      return pkg.name;
    }
    const session = this.allSessionById.get(String(b.sessionId));
    if (session && this.isOneToOneSession(session)) {
      return this.setLabel('oneToOneSessionLabel');
    }
    return this.setLabel('sessionFallbackTitleLabel');
  }

  allBookingTitle(b: TrainerBookingRow): string {
    const session = this.allSessionById.get(String(b.sessionId));
    const name = this.allBookingPackageName(b);
    if (session) {
      return `${name} · ${session.localDate} · ${this.sessionWindow(session)}`;
    }
    return name;
  }

  allBookingDate(b: TrainerBookingRow): string {
    return this.allSessionById.get(String(b.sessionId))?.localDate || '—';
  }

  allBookingSlot(b: TrainerBookingRow): string {
    const session = this.allSessionById.get(String(b.sessionId));
    return session ? this.sessionWindow(session) : '—';
  }

  allBookingSeats(b: TrainerBookingRow): string {
    const session = this.allSessionById.get(String(b.sessionId));
    return session ? `${session.bookedCount}/${session.maxMembers}` : '—';
  }

  bookingStatusLabel(status: string): string {
    switch (status) {
      case 'confirmed':
        return this.setLabel('statusConfirmedLabel');
      case 'cancel':
        return this.setLabel('statusCancelledLabel');
      case 'Present':
        return this.setLabel('statusCompletedLabel');
      case 'Absent':
        return this.setLabel('statusNoShowLabel');
      default:
        return status || '—';
    }
  }

  openBookingDetail(b: TrainerBookingRow): void {
    this.detailSeriesGroup = null;
    this.detailBooking = b;
  }

  openSeriesGroupDetail(g: SeriesEnrollmentGroup): void {
    this.detailBooking = null;
    this.detailSeriesGroup = g;
  }

  closeBookingDetail(): void {
    this.detailBooking = null;
    this.detailSeriesGroup = null;
  }

  bookSession(s: TrainerSessionRow): void {
    if (!this.memberId || !this.branchId || !s?._id) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('bookFailedLabel'),
      });
      return;
    }
    if (this.bookingBusy) {
      return;
    }
    if (!this.isSessionStartInFuture(s.localDate, s.startMinutes)) {
      this.warnPastSession();
      return;
    }
    this.openBookDialog(s);
  }

  openEnrollDialog(card: DaySeriesCard): void {
    if (!this.memberId || !this.branchId) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('bookFailedLabel'),
      });
      return;
    }
    if (this.bookingBusy) {
      return;
    }
    if (this.isFullyEnrolledInSeries(card.seriesId)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('alreadyEnrolledLabel'),
      });
      return;
    }

    this.bookMode = 'series';
    this.bookTarget = null;
    this.bookPackageId = null;
    this.oneToOnePackageOptions = [];
    this.sameSlotSessionOptions = [];
    this.selectedBookSessionIds = [];
    this.bookingBusy = false;
    this.initEnrollDialogState(card);
    this.bookDialogVisible = true;
    this.refreshEnrollDialogFromApi(card.seriesId);
  }

  private initEnrollDialogState(card: DaySeriesCard): void {
    this.enrollTarget = {
      ...card,
      sessions: [...card.sessions],
    };
    this.bookBaseAmount = card.price;
    this.bookPayNow = card.price;
    this.lastBookBaseForPayNow = card.price;
    this.bookDiscount = 0;
    this.resetSplitState();
    this.bookPaymentMode = this.defaultCashPaymentMode();
    this.selectedReceiptFile = null;
    this.selectedReceiptName = '';
    this.feeReceiptImageUrl = '';
    this.receiptUploading = false;
    this.bookComment = '';
    this.bookDiscountError = false;
    this.bookClassPackageLabel = card.packageName;
    this.enrollTotalProgramMonths = 1;
    this.enrollMonthOptions = [];
    this.selectedEnrollMonths = [];
    this.enrollMonthPanelOpen = false;
    this.enrollMonthWise = card.monthWise === true;
    this.applyTaxFromPaymentMode(this.bookPaymentMode);
    this.recalcBookFee();
  }

  private refreshEnrollDialogFromApi(seriesId: string): void {
    const customerQ = this.memberId
      ? `?customerId=${this.memberId}`
      : '';
    this.appServiceManager
      .get(`branches/${this.branchId}/trainer-session-series/${seriesId}${customerQ}`)
      .subscribe(
        (res: {
          packageName?: string;
          packagePrice?: number;
          minSeatsAvailable?: number;
          totalProgramMonths?: number;
          monthWise?: boolean;
          programMonths?: Array<{
            key: string;
            sessionCount?: number;
            minSeats?: number;
          }>;
          futureSessions?: Array<{
            _id: string;
            localDate: string;
            startMinutes: number;
            durationMinutes: number;
            bookedCount: number;
            maxMembers: number;
          }>;
        }) => {
          if (!this.isSeriesDialogOpenFor(seriesId)) {
            return;
          }
          const card = this.enrollTarget;
          if (!card) {
            return;
          }
          const minSeats = Number(res?.minSeatsAvailable) || 0;
          const future = Array.isArray(res?.futureSessions)
            ? res.futureSessions
            : [];
          const sessions: TrainerSessionRow[] = future.map((s) => ({
            _id: this.refId(s._id),
            localDate: s.localDate,
            startMinutes: s.startMinutes,
            durationMinutes: s.durationMinutes,
            maxMembers: s.maxMembers,
            bookedCount: s.bookedCount,
            seriesId,
            trainerId: card.trainerId,
            status: 'scheduled',
          }));
          this.enrollTarget = {
            ...card,
            sessions: sessions.length ? sessions : card.sessions,
            minSeats,
            packageName: res?.packageName || card.packageName,
            price: Number(res?.packagePrice) || card.price,
            monthWise: res?.monthWise === true,
          };
          this.bookClassPackageLabel = this.enrollTarget.packageName;
          this.enrollMonthWise = res?.monthWise === true;
          this.enrollTotalProgramMonths = Math.max(
            1,
            Number(res?.totalProgramMonths) || 1,
          );
          this.enrollMonthOptions = (Array.isArray(res?.programMonths)
            ? res.programMonths
            : []
          )
            .filter((m) => m?.key)
            .map((m) => ({
              key: String(m.key),
              label: this.enrollMonthLabel(String(m.key)),
              sessionCount: Number(m.sessionCount) || 0,
              minSeats: Number(m.minSeats) || 0,
            }));
          if (this.showEnrollMonthPicker) {
            const nowKey = moment.tz(TIMEZONE_PKT).format('YYYY-MM');
            const current =
              this.enrollMonthOptions.find((m) => m.key === nowKey) ||
              this.enrollMonthOptions[0];
            this.selectedEnrollMonths = current ? [current.key] : [];
          } else {
            this.selectedEnrollMonths = [];
          }
          this.applyEnrollMonthSelection();
          this.cdr.markForCheck();
        },
        (err: any) => {
          if (this.isSeriesDialogOpenFor(seriesId)) {
            this.toastError(err, this.setLabel('enrollFailedLabel'));
          }
          this.cdr.markForCheck();
        },
      );
  }

  get enrollMonthTriggerLabel(): string {
    const n = this.selectedEnrollMonths.length;
    if (n < 1) {
      return this.setLabel('selectProgramMonthsLabel');
    }
    if (n === 1) {
      const key = this.selectedEnrollMonths[0];
      return (
        this.enrollMonthOptions.find((o) => o.key === key)?.label || key || ''
      );
    }
    return this.setLabel('programMonthsSelectedCountLabel').replace(
      '{count}',
      '' + n,
    );
  }

  toggleEnrollMonthPanel(ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    if (this.bookingBusy) {
      return;
    }
    this.enrollMonthPanelOpen = !this.enrollMonthPanelOpen;
  }

  isEnrollMonthSelected(key: string): boolean {
    return this.selectedEnrollMonths.includes(key);
  }

  onEnrollMonthRowClick(key: string): void {
    if (this.bookingBusy) {
      return;
    }
    if (this.isEnrollMonthSelected(key)) {
      this.selectedEnrollMonths = this.selectedEnrollMonths.filter(
        (k) => k !== key,
      );
    } else {
      this.selectedEnrollMonths = [...this.selectedEnrollMonths, key];
    }
    this.applyEnrollMonthSelection();
  }

  private applyEnrollMonthSelection(): void {
    const price = Number(this.enrollTarget?.price) || 0;
    if (!this.showEnrollMonthPicker) {
      this.bookBaseAmount = price;
    } else {
      const n = this.selectedEnrollMonths.length;
      this.bookBaseAmount = n < 1 ? 0 : Math.round(price * n);
    }
    if (
      this.bookPayNow == null ||
      this.bookPayNow === 0 ||
      this.bookPayNow === this.lastBookBaseForPayNow
    ) {
      this.bookPayNow = this.bookBaseAmount;
    }
    this.lastBookBaseForPayNow = this.bookBaseAmount;
    this.recalcBookFee();
  }

  private enrollMonthLabel(key: string): string {
    const [y, m] = String(key).split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    if (Number.isNaN(d.getTime())) {
      return key;
    }
    return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  }

  private isSeriesDialogOpenFor(seriesId: string): boolean {
    return (
      this.bookDialogVisible &&
      this.isSeriesBookMode &&
      this.enrollTarget?.seriesId === seriesId
    );
  }

  confirmEnroll(): void {
    const card = this.enrollTarget;
    if (!card?.seriesId) {
      return;
    }
    if (this.bookPayNowError) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.bookPayNowErrorLabel,
      });
      return;
    }
    if (!this.multiplePaymentModesEnabled && !this.bookPaymentMode) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('paymentModeRequiredLabel'),
      });
      return;
    }
    if (this.multiplePaymentModesEnabled && !this.isSplitFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('splitValidationErrorSummaryLabel'),
        detail: this.splitValidationMessage,
      });
      return;
    }
    if (this.bookDiscountError) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('discountInvalidLabel'),
      });
      return;
    }
    if (this.selectedReceiptFile && !this.feeReceiptImageUrl) {
      this.uploadReceiptThen(() => this.doEnrollSeries(card));
      return;
    }
    this.doEnrollSeries(card);
  }

  private doEnrollSeries(card: DaySeriesCard): void {
    this.bookingBusy = true;
    const body: Record<string, unknown> = {
      customerId: this.memberId,
      discount: Number(this.bookDiscount) || 0,
    };
    this.appendCollectFields(body);
    if (this.feeReceiptImageUrl) {
      body.feeReceiptImageUrl = this.feeReceiptImageUrl;
    }
    if (this.bookComment?.trim()) {
      body.description = this.bookComment.trim();
    }
    if (this.showEnrollMonthPicker) {
      if (this.selectedEnrollMonths.length < 1) {
        this.bookingBusy = false;
        return;
      }
      body.months = [...this.selectedEnrollMonths];
    }
    this.appServiceManager
      .post(
        `branches/${this.branchId}/trainer-session-series/${card.seriesId}/enroll`,
        body,
      )
      .subscribe(
        (res: any) => {
          this.bookingBusy = false;
          const sessionCount = Number(res?.sessionCount) || card.sessions.length;
          this.closeBookDialog();
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('enrollSuccessLabel').replace(
              '{count}',
              String(sessionCount),
            ),
          });
          this.refreshAfterMutation(card.trainerId);
          const fees = this.normalizeFeesResponse(res);
          if (fees.length) {
            const printFee = fees.find((f) => !f.parentFeeId) || fees[0];
            setTimeout(() => this.printFeeReceipt(printFee, fees), 350);
          }
        },
        (err: any) => {
          this.bookingBusy = false;
          this.toastError(err, this.setLabel('enrollFailedLabel'));
        },
      );
  }

  private loadEnrolledSeriesIds(): void {
    if (!this.memberId || !this.branchId) {
      this.enrolledSeriesIds = new Set();
      this.fullyEnrolledSeriesIds = new Set();
      return;
    }
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-session-series/enrollments?customerId=${this.memberId}`,
      )
      .subscribe(
        (res: { seriesIds?: string[]; fullyEnrolledSeriesIds?: string[] }) => {
          const ids = Array.isArray(res?.seriesIds) ? res.seriesIds : [];
          const full = Array.isArray(res?.fullyEnrolledSeriesIds)
            ? res.fullyEnrolledSeriesIds
            : ids;
          this.enrolledSeriesIds = new Set(ids.map(String));
          this.fullyEnrolledSeriesIds = new Set(full.map(String));
          this.applyAvailabilityFromCache();
        },
        () => {
          this.enrolledSeriesIds = new Set();
          this.fullyEnrolledSeriesIds = new Set();
        },
      );
  }

  closeBookDialog(): void {
    this.bookDialogVisible = false;
    this.bookMode = 'session';
    this.enrollTarget = null;
    this.bookTarget = null;
    this.bookPackageId = null;
    this.oneToOnePackageOptions = [];
    this.bookClassPackageLabel = '';
    this.sameSlotSessionOptions = [];
    this.selectedBookSessionIds = [];
    this.sameSlotPanelOpen = false;
    this.enrollTotalProgramMonths = 1;
    this.enrollMonthOptions = [];
    this.selectedEnrollMonths = [];
    this.enrollMonthPanelOpen = false;
    this.enrollMonthWise = false;
    this.bookBaseAmount = 0;
    this.bookPayNow = 0;
    this.lastBookBaseForPayNow = 0;
    this.bookDiscount = 0;
    this.bookPaymentMode = this.defaultCashPaymentMode();
    this.bookTax = 0;
    this.bookTotalPaid = 0;
    this.bookAmountDueTotal = 0;
    this.bookThisPaymentTotal = 0;
    this.bookBalanceRemaining = 0;
    this.bookDiscountError = false;
    this.bookPayNowError = false;
    this.resetSplitState();
    this.selectedReceiptFile = null;
    this.selectedReceiptName = '';
    this.feeReceiptImageUrl = '';
    this.receiptUploading = false;
    this.bookComment = '';
  }

  closePayRemainingDialog(): void {
    this.payRemainingDialogVisible = false;
    this.payRemainingTarget = null;
    this.payRemainingPayNow = 0;
    this.payRemainingPayNowError = false;
    this.payRemainingPaymentMode = this.defaultCashPaymentMode();
    this.payRemainingBusy = false;
    this.resetSplitState();
    this.selectedReceiptFile = null;
    this.selectedReceiptName = '';
    this.feeReceiptImageUrl = '';
    this.receiptUploading = false;
    this.bookComment = '';
  }

  openPayRemainingDialog(fee: TrainerBookingFeeRow): void {
    const primary = this.primaryFeeRecord(fee);
    const balance = Number(primary.balance) || 0;
    if (!primary?._id || balance <= 0) {
      return;
    }
    this.payRemainingTarget = primary;
    this.payRemainingPayNow = balance;
    this.payRemainingPaymentMode = this.defaultCashPaymentMode();
    this.payRemainingPayNowError = false;
    this.resetSplitState();
    this.selectedReceiptFile = null;
    this.selectedReceiptName = '';
    this.feeReceiptImageUrl = '';
    this.bookComment = '';
    this.payRemainingDialogVisible = true;
    this.applyTaxFromPaymentMode(this.payRemainingPaymentMode);
    this.recalcPayRemainingFee();
  }

  onPayRemainingPayNowChange(): void {
    this.recalcPayRemainingFee();
  }

  onPayRemainingPaymentModeChange(): void {
    this.applyTaxFromPaymentMode(this.payRemainingPaymentMode);
    if (!this.needsPayRemainingReceipt()) {
      this.selectedReceiptFile = null;
      this.selectedReceiptName = '';
      this.feeReceiptImageUrl = '';
    }
    this.recalcPayRemainingFee();
  }

  confirmPayRemaining(): void {
    const primary = this.payRemainingTarget;
    if (!primary?._id) {
      return;
    }
    if (this.payRemainingPayNowError) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('feegreaterLabel'),
      });
      return;
    }
    if (!this.multiplePaymentModesEnabled && !this.payRemainingPaymentMode) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('paymentModeRequiredLabel'),
      });
      return;
    }
    if (this.multiplePaymentModesEnabled && !this.isSplitFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('splitValidationErrorSummaryLabel'),
        detail: this.splitValidationMessage,
      });
      return;
    }
    if (this.selectedReceiptFile && !this.feeReceiptImageUrl) {
      this.uploadReceiptThen(() => this.doPayRemaining(primary));
      return;
    }
    this.doPayRemaining(primary);
  }

  private doPayRemaining(primary: TrainerBookingFeeRow): void {
    this.payRemainingBusy = true;
    const body: Record<string, unknown> = {};
    this.appendPayRemainingFields(body);
    if (this.feeReceiptImageUrl) {
      body.feeReceiptImageUrl = this.feeReceiptImageUrl;
    }
    if (this.bookComment?.trim()) {
      body.description = this.bookComment.trim();
    }
    this.appServiceManager
      .post(
        `branches/${this.branchId}/trainer-booking-fees/${primary._id}/pay-remaining`,
        body,
      )
      .subscribe(
        (res: any) => {
          this.payRemainingBusy = false;
          this.closePayRemainingDialog();
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('payRemainingSuccessLabel'),
          });
          const fees = this.normalizeFeesResponse(res);
          if (fees.length) {
            const printFee = fees.find((f) => !f.parentFeeId) || fees[0];
            setTimeout(
              () => this.printFeeReceipt(printFee, fees),
              350,
            );
          }
          this.refreshAfterMutation(primary.trainerId);
        },
        (err: any) => {
          this.payRemainingBusy = false;
          this.toastError(err, this.setLabel('payRemainingFailedLabel'));
        },
      );
  }

  confirmBook(): void {
    const target = this.bookTarget;
    if (!target?._id) {
      return;
    }
    if (!this.isSessionStartInFuture(target.localDate, target.startMinutes)) {
      this.warnPastSession();
      return;
    }
    const isOne = this.isOneToOneSession(target);
    if (isOne && !this.bookPackageId) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('oneToOnePackageRequiredLabel'),
      });
      return;
    }
    if (this.bookPayNowError) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.bookPayNowErrorLabel,
      });
      return;
    }
    if (!this.multiplePaymentModesEnabled && !this.bookPaymentMode) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('paymentModeRequiredLabel'),
      });
      return;
    }
    if (this.multiplePaymentModesEnabled && !this.isSplitFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('splitValidationErrorSummaryLabel'),
        detail: this.splitValidationMessage,
      });
      return;
    }
    if (this.bookDiscountError) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('discountInvalidLabel'),
      });
      return;
    }

    const sessionId = target._id;
    const trainerId = target.trainerId;
    const packageId = isOne ? this.bookPackageId || undefined : undefined;
    const selectedIds = this.uniqueSelectedSessionIds(sessionId);
    const run =
      selectedIds.length > 1
        ? () => this.doBookMany(selectedIds, packageId, trainerId)
        : () => this.doBook(sessionId, packageId, trainerId);

    // ponytail: receipt optional for Online/Check — upload only when user picked a file
    if (this.selectedReceiptFile && !this.feeReceiptImageUrl) {
      this.uploadReceiptThen(run);
      return;
    }
    run();
  }

  onBookPackageChange(): void {
    const target = this.bookTarget;
    if (
      target &&
      !this.isSeriesBookMode &&
      this.isOneToOneSession(target) &&
      this.bookPackageId &&
      !this.sameSlotSessionOptions.length
    ) {
      this.loadSameSlotAvailable(target);
    }
    this.applyBookPackagePrice(true);
    this.recalcBookFee();
  }

  onBookDiscountChange(raw?: unknown): void {
    this.bookDiscount = this.parseNonNegativeMoneyInput(raw ?? this.bookDiscount);
    this.recalcBookFee();
  }

  onBookPayNowChange(raw?: unknown): void {
    this.bookPayNow = this.parsePositiveMoneyInput(raw ?? this.bookPayNow);
    this.recalcBookFee();
  }

  /** Digits only; empty → null (allows full clear while typing). */
  private parsePositiveMoneyInput(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const digits = String(raw).replace(/[^\d]/g, '');
    if (!digits) {
      return null;
    }
    return Number(digits);
  }

  /** Digits only; empty → 0 (discount may be blanked to 0). */
  private parseNonNegativeMoneyInput(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    const digits = String(raw).replace(/[^\d]/g, '');
    if (!digits) {
      return null;
    }
    return Number(digits);
  }

  get bookPayNowErrorLabel(): string {
    const packageDue = Number(this.bookBaseAmount) || 0;
    const payNow = this.bookPayNow;
    if (payNow === null || payNow === undefined || Number(payNow) <= 0) {
      return this.setLabel('feeMustBeGreaterThanZeroLabel');
    }
    if (Number(payNow) > packageDue) {
      return this.setLabel('feegreaterLabel');
    }
    return this.setLabel('feeMustBeGreaterThanZeroLabel');
  }

  onMultiplePaymentModesToggle(checked: boolean): void {
    this.multiplePaymentModesEnabled = checked;
    if (checked) {
      this.initPaymentSplits();
    } else {
      this.paymentSplits = [];
    }
    if (this.isPayRemainingDialog) {
      this.recalcPayRemainingFee();
    } else {
      this.recalcBookFee();
    }
  }

  initPaymentSplits(): void {
    const defaultMode =
      this.activePaymentMode || this.paymentOptions?.[0]?.value || '';
    const first = createEmptySplitRow(defaultMode);
    const second = createEmptySplitRow(defaultMode);
    if (defaultMode) {
      applyTaxSettingsToRow(first, this.branchPayments, defaultMode);
      applyTaxSettingsToRow(second, this.branchPayments, defaultMode);
    }
    this.paymentSplits = [first, second];
    this.recalculateAllSplitRows();
  }

  addPaymentSplitRow(): void {
    const defaultMode = this.paymentOptions?.[0]?.value || '';
    const row = createEmptySplitRow(defaultMode);
    if (defaultMode) {
      applyTaxSettingsToRow(row, this.branchPayments, defaultMode);
    }
    this.paymentSplits = [...this.paymentSplits, row];
    this.recalculateAllSplitRows();
  }

  removePaymentSplitRow(rowId: string): void {
    if (this.paymentSplits.length <= 2) {
      return;
    }
    this.paymentSplits = this.paymentSplits.filter((r) => r.id !== rowId);
    this.recalculateAllSplitRows();
  }

  onSplitPaymentModeChange(row: PaymentSplitRow, mode: string): void {
    applyTaxSettingsToRow(row, this.branchPayments, mode);
    this.recalculateAllSplitRows();
  }

  onSplitPayChange(row: PaymentSplitRow, value: number | string | null): void {
    if (value === '' || value === null || value === undefined) {
      row.pay = null;
    } else {
      row.pay = Math.round(Number(value) || 0);
    }
    this.recalculateAllSplitRows();
  }

  trackSplitRowById(_index: number, row: PaymentSplitRow): string {
    return row.id;
  }

  isSplitAllocationValid(): boolean {
    return isSplitAllocationValid(
      this.paymentSplits,
      this.splitAllocationTarget,
    );
  }

  hasDuplicateSplitPaymentModes(): boolean {
    const seen = new Set<string>();
    for (const row of this.paymentSplits || []) {
      const mode = String(row?.paymentMode || '').trim();
      if (!mode) {
        continue;
      }
      if (seen.has(mode)) {
        return true;
      }
      seen.add(mode);
    }
    return false;
  }

  hasIncompleteSplitRows(): boolean {
    return (this.paymentSplits || []).some((row) => {
      const mode = String(row?.paymentMode || '').trim();
      const pay = Number(row?.pay);
      return !mode || !Number.isFinite(pay) || pay <= 0;
    });
  }

  isSplitFormValid(): boolean {
    return (
      this.multiplePaymentModesEnabled &&
      this.paymentSplits.length >= 2 &&
      !this.hasIncompleteSplitRows() &&
      !this.hasDuplicateSplitPaymentModes() &&
      this.isSplitAllocationValid()
    );
  }

  shouldUseSplitPayment(): boolean {
    return this.multiplePaymentModesEnabled && this.paymentSplits.length >= 2;
  }

  recalculateAllSplitRows(): void {
    if (!this.multiplePaymentModesEnabled) {
      return;
    }
    const fundPay = this.activePayNow;
    const fundDiscount = this.activeDiscount;
    const netTarget = getSplitAllocationTarget(fundPay, fundDiscount);
    this.paymentSplits = this.paymentSplits.map((row) => {
      const updated = { ...row };
      const grossPay = netSplitPayToGross(updated.pay, fundPay, netTarget);
      const lineDiscount = proportionalLineDiscount(
        grossPay,
        fundPay,
        fundDiscount,
      );
      const taxRow: PaymentSplitRow = { ...updated, pay: grossPay };
      recalculateRowTax(taxRow, lineDiscount, {
        isDiscountFormulaReverse: this.isDiscountFormulaReverse,
        fundPay,
        fundDiscount,
      });
      updated.tax = taxRow.tax;
      updated.includedTax = taxRow.includedTax;
      updated.lineDiscount = lineDiscount;
      updated.lineTotal = calculateSplitRowTotal(taxRow, lineDiscount);
      return updated;
    });
    this.bookThisPaymentTotal = this.splitGrandTotal;
    this.bookTax = this.splitTotalTax;
    this.bookTotalPaid = this.splitGrandTotal;
    this.bookBalanceRemaining = Math.max(0, this.activePayMax - fundPay);
    if (this.isPayRemainingDialog) {
      this.payRemainingPayNowError =
        fundPay <= 0 || fundPay > this.activePayMax;
    }
  }

  buildSplitLinesForSubmit(): PaymentSplitRow[] {
    return convertNetSplitsToGross(
      this.paymentSplits.map((row) => ({ ...row })),
      this.activePayNow,
      this.activeDiscount,
    );
  }

  resetSplitState(): void {
    this.multiplePaymentModesEnabled = false;
    this.paymentSplits = [];
  }

  onBookPaymentModeChange(): void {
    this.applyTaxFromPaymentMode(this.bookPaymentMode);
    if (!this.needsBookReceipt()) {
      this.selectedReceiptFile = null;
      this.selectedReceiptName = '';
      this.feeReceiptImageUrl = '';
    }
    this.recalcBookFee();
  }

  onReceiptSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input?.files?.[0] || null;
    this.selectedReceiptFile = file;
    this.selectedReceiptName = file?.name || '';
    this.feeReceiptImageUrl = '';
  }

  receiptUrl(fileName?: string): string {
    if (!fileName) {
      return '';
    }
    const base = String(this.config.Server || '').replace(/\/+$/, '');
    const path = String(
      this.config.trainerFeeReceiptImagePath ||
        localStorage.getItem('trainerFeeReceiptImagePath') ||
        '',
    ).replace(/^\/+|\/+$/g, '');
    const file = String(fileName).replace(/^\/+/, '');
    if (!path) {
      return `${base}/${file}`;
    }
    return `${base}/${path}/${file}`;
  }

  private warnPastSession(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.setLabel('errorSummaryLabel'),
      detail: this.setLabel('bookPastSessionErrorLabel'),
    });
  }

  /** Session localDate + startMinutes as Asia/Karachi wall clock vs now. */
  private isSessionStartInFuture(
    localDate: string,
    startMinutes: number,
  ): boolean {
    const start = moment
      .tz(localDate, 'YYYY-MM-DD', TIMEZONE_PKT)
      .startOf('day')
      .add(startMinutes, 'minutes');
    return start.isAfter(moment.tz(TIMEZONE_PKT));
  }

  private openBookDialog(s: TrainerSessionRow): void {
    this.bookMode = 'session';
    this.enrollTarget = null;
    this.bookTarget = s;
    this.bookDiscount = 0;
    this.resetSplitState();
    this.bookPaymentMode = this.defaultCashPaymentMode();
    this.selectedReceiptFile = null;
    this.selectedReceiptName = '';
    this.feeReceiptImageUrl = '';
    this.receiptUploading = false;
    this.bookingBusy = false;
    this.bookComment = '';
    this.bookDiscountError = false;
    this.bookPayNowError = false;
    this.sameSlotSessionOptions = [];
    this.selectedBookSessionIds = s._id ? [s._id] : [];
    this.sameSlotPanelOpen = false;

    if (this.isOneToOneSession(s)) {
      const opts: { label: string; value: string }[] =
        this.trainerOneToOnePackages(s.trainerId).map((pkg) => ({
          label: pkg.name || '',
          value: this.refId(pkg._id),
        }));
      opts.sort((a, b) => a.label.localeCompare(b.label));
      if (!opts.length) {
        this.messageService.add({
          severity: 'error',
          summary: this.setLabel('errorSummaryLabel'),
          detail: this.setLabel('noOneToOnePackagesLabel'),
        });
        this.bookTarget = null;
        return;
      }
      this.oneToOnePackageOptions = opts;
      this.bookPackageId = opts.length === 1 ? opts[0].value : null;
      this.bookClassPackageLabel = '';
      if (this.bookPackageId) {
        this.loadSameSlotAvailable(s);
      }
    } else {
      this.oneToOnePackageOptions = [];
      this.bookPackageId = this.refId(s.packageId);
      const pkg = this.packageById.get(this.bookPackageId);
      this.bookClassPackageLabel = pkg?.name || this.setLabel('sessionFallbackTitleLabel');
      this.loadSameSlotAvailable(s);
    }

    this.applyBookPackagePrice(true);
    this.applyTaxFromPaymentMode(this.bookPaymentMode);
    this.recalcBookFee();
    this.bookDialogVisible = true;
  }

  private applyBookPackagePrice(resetFeeToActual = false): void {
    const pkgId = this.bookPackageId
      ? this.refId(this.bookPackageId)
      : this.refId(this.bookTarget?.packageId);
    const pkg = pkgId ? this.packageById.get(pkgId) : null;
    const unit = Number(pkg?.price) || 0;
    const n = Math.max(this.selectedBookSessionIds.length, 1);
    this.bookBaseAmount = unit * n;
    if (
      resetFeeToActual ||
      this.bookPayNow == null ||
      this.bookPayNow === 0
    ) {
      this.bookPayNow = this.bookBaseAmount;
      this.lastBookBaseForPayNow = this.bookBaseAmount;
    }
  }

  private uniqueSelectedSessionIds(seedId: string): string[] {
    const ids = this.selectedBookSessionIds.map(String).filter(Boolean);
    if (seedId && !ids.includes(seedId)) {
      ids.unshift(seedId);
    }
    return [...new Set(ids)];
  }

  private loadSameSlotAvailable(seed: TrainerSessionRow): void {
    if (!this.branchId || !seed?._id || !this.memberId) {
      this.sameSlotSessionOptions = [];
      return;
    }
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-sessions/${seed._id}/same-slot-available?customerId=${this.memberId}`,
      )
      .subscribe(
        (rows: TrainerSessionRow[]) => {
          if (!this.bookDialogVisible || this.bookTarget?._id !== seed._id) {
            return;
          }
          const list = Array.isArray(rows) ? rows : [];
          const opts = list.map((s) => ({
            label: `${s.localDate} · ${this.sessionWindow(s)}`,
            value: String(s._id),
          }));
          if (seed._id && !opts.some((o) => o.value === seed._id)) {
            opts.unshift({
              label: `${seed.localDate} · ${this.sessionWindow(seed)}`,
              value: seed._id,
            });
          }
          this.sameSlotSessionOptions = opts;
          if (seed._id && !this.selectedBookSessionIds.includes(seed._id)) {
            this.selectedBookSessionIds = [seed._id, ...this.selectedBookSessionIds];
          }
          this.applyBookPackagePrice(true);
          this.recalcBookFee();
        },
        () => {
          this.sameSlotSessionOptions = [];
        },
      );
  }

  private applyTaxFromPaymentMode(mode: string | null): void {
    if (!mode) {
      this.bookTaxType = 'NO_TAX';
      this.bookTaxRate = 0;
      return;
    }
    const extracted = mode.includes('-') ? mode.split('-')[1] : mode;
    const payment = this.branchPayments.find((p) => p?.name === extracted);
    this.bookTaxType = payment?.taxSettings?.taxCalculationType || 'NO_TAX';
    this.bookTaxRate = Number(payment?.taxSettings?.taxRate) || 0;
  }

  /** Preview totals — discount always vs Fee field (payNow), not package. */
  private recalcBookFee(): void {
    const packageDue = Number(this.bookBaseAmount) || 0;
    const prevPackage = this.lastBookBaseForPayNow;

    // Follow package price only when it changed and fee was still the previous full amount.
    if (
      packageDue > 0 &&
      prevPackage > 0 &&
      packageDue !== prevPackage &&
      this.bookPayNow != null &&
      this.bookPayNow === prevPackage
    ) {
      this.bookPayNow = packageDue;
    } else if (this.bookPayNow != null && this.bookPayNow > packageDue) {
      this.bookPayNow = packageDue;
    }
    this.lastBookBaseForPayNow = packageDue;

    const payEmpty =
      this.bookPayNow === null || this.bookPayNow === undefined;
    const payNow = payEmpty ? 0 : Number(this.bookPayNow) || 0;
    this.bookPayNowError = payEmpty || payNow <= 0 || payNow > packageDue;

    const discountEmpty =
      this.bookDiscount === null || this.bookDiscount === undefined;
    const discount = discountEmpty ? 0 : Number(this.bookDiscount) || 0;
    this.bookDiscountError = discount < 0 || discount > payNow;

    // Full package due (discount does not reduce obligation ceiling).
    const fullTotals = this.computeFeeTotals(
      packageDue,
      0,
      this.bookTaxType,
      this.bookTaxRate,
    );
    this.bookAmountDueTotal = fullTotals.totalPaid;

    if (this.multiplePaymentModesEnabled) {
      this.recalculateAllSplitRows();
      this.bookThisPaymentTotal = this.splitGrandTotal;
      this.bookBalanceRemaining = Math.max(0, packageDue - payNow);
      return;
    }

    const thisTotals = this.computeFeeTotals(
      payNow,
      discount,
      this.bookTaxType,
      this.bookTaxRate,
    );
    this.bookTax = thisTotals.tax;
    this.bookTotalPaid = thisTotals.totalPaid;
    this.bookThisPaymentTotal = thisTotals.totalPaid;
    this.bookBalanceRemaining = Math.max(0, packageDue - payNow);
  }

  private recalcPayRemainingFee(): void {
    const balance = Number(this.payRemainingTarget?.balance) || 0;
    const payNow = Number(this.payRemainingPayNow) || 0;
    this.payRemainingPayNowError = payNow <= 0 || payNow > balance;
    this.bookBalanceRemaining = Math.max(0, balance - payNow);
    if (this.multiplePaymentModesEnabled) {
      this.recalculateAllSplitRows();
      return;
    }
    const thisTotals = this.computeFeeTotals(
      payNow,
      0,
      this.bookTaxType,
      this.bookTaxRate,
    );
    this.bookTax = thisTotals.tax;
    this.bookTotalPaid = thisTotals.totalPaid;
    this.bookThisPaymentTotal = thisTotals.totalPaid;
  }

  private computeFeeTotals(
    pay: number,
    discount: number,
    taxType: string,
    taxRate: number,
  ): { tax: number; totalPaid: number } {
    const net = pay - discount;
    if (taxType === 'NO_TAX' || !taxRate) {
      return { tax: 0, totalPaid: Math.max(0, net) };
    }
    if (discount === pay) {
      return { tax: 0, totalPaid: 0 };
    }
    const taxable = this.isDiscountFormulaReverse ? pay : net;
    const tax = Math.round((taxable * taxRate) / 100);
    if (taxType === 'WITH_TAX') {
      return { tax, totalPaid: Math.max(0, net + tax) };
    }
    return { tax, totalPaid: Math.max(0, net) };
  }

  private appendCollectFields(body: Record<string, unknown>): void {
    const base = Number(this.bookBaseAmount) || 0;
    const payNow = Number(this.bookPayNow) || 0;
    if (this.shouldUseSplitPayment()) {
      const grossSplits = this.buildSplitLinesForSubmit();
      body.splits = grossSplits.map((row) => ({
        paymentMode: row.paymentMode,
        pay: row.pay,
        tax: row.tax,
        ...(row.feeReceiptImageUrl
          ? { feeReceiptImageUrl: row.feeReceiptImageUrl }
          : {}),
      }));
      body.paymentMode =
        grossSplits[0]?.paymentMode || this.bookPaymentMode || '';
      body.payNow = payNow;
      return;
    }
    body.paymentMode = this.bookPaymentMode || '';
    if (payNow > 0 && payNow < base) {
      body.payNow = payNow;
    }
  }

  private appendPayRemainingFields(body: Record<string, unknown>): void {
    const payNow = Number(this.payRemainingPayNow) || 0;
    if (this.shouldUseSplitPayment()) {
      const grossSplits = this.buildSplitLinesForSubmit();
      body.splits = grossSplits.map((row) => ({
        paymentMode: row.paymentMode,
        pay: row.pay,
        ...(row.feeReceiptImageUrl
          ? { feeReceiptImageUrl: row.feeReceiptImageUrl }
          : {}),
      }));
      body.paymentMode =
        grossSplits[0]?.paymentMode || this.payRemainingPaymentMode || '';
      body.payNow = payNow;
      return;
    }
    body.paymentMode = this.payRemainingPaymentMode || '';
    body.payNow = payNow;
  }

  private loadBranchPayments(): void {
    if (!this.branchId) {
      return;
    }
    this.appServiceManager.get(`branches/${this.branchId}`).subscribe(
      (res: any) => {
        this.printBranch = res;
        this.invoiceMessage = res?.invoiceData?.invoiceMessage || '';
        if (res?.company) {
          const companyId =
            typeof res.company === 'object'
              ? res.company._id || res.company
              : res.company;
          this.appServiceManager.get(`companies/${companyId}`).subscribe(
            (co: any) => {
              this.printCompany = co;
            },
            () => {
              this.printCompany = null;
            },
          );
        }
        this.branchPayments = Array.isArray(res?.payments) ? res.payments : [];
        this.feeDeleteRoles = Array.isArray(
          res?.permissions?.trainerModule?.feeDeleteRoles,
        )
          ? res.permissions.trainerModule.feeDeleteRoles.map(String).filter(Boolean)
          : [];
        this.isDiscountFormulaReverse = Boolean(
          res?.permissions?.isDiscountFormulaReverse,
        );
        this.paymentOptions = [];
        this.branchPayments.forEach((payment) => {
          const value = `${payment.type}-${payment.name}`;
          if (!this.paymentOptions.find((o) => o.value === value)) {
            this.paymentOptions.push({
              label: `${payment.type} - ${payment.name}`,
              value,
            });
          }
        });
        if (!this.paymentOptions.length) {
          this.paymentOptions = [
            { label: 'Cash - Reception', value: 'Cash-Reception' },
          ];
        }
        this.bookPaymentMode = this.defaultCashPaymentMode();
        this.hideTaxInfo = this.branchPayments.every(
          (p) => p?.taxSettings?.taxCalculationType === 'NO_TAX',
        );
      },
      () => {
        this.feeDeleteRoles = [];
        this.paymentOptions = [
          { label: 'Cash - Reception', value: 'Cash-Reception' },
        ];
        this.bookPaymentMode = this.defaultCashPaymentMode();
      },
    );
  }

  /** Prefer Cash-* from branch modes (add-fee style default). */
  private defaultCashPaymentMode(): string | null {
    const cash = this.paymentOptions.find((o) =>
      String(o.value || '')
        .toLowerCase()
        .startsWith('cash-'),
    );
    if (cash) {
      return cash.value;
    }
    const reception = this.paymentOptions.find(
      (o) => o.value === 'Cash-Reception',
    );
    return reception?.value || this.paymentOptions[0]?.value || null;
  }

  loadFeeHistory(): void {
    if (!this.memberId || !this.branchId) {
      this.feeHistoryAllRows = [];
      this.feeHistoryFirst = 0;
      return;
    }
    if (!this.feeHistoryFrom && !this.feeHistoryTo) {
      this.resetFeeHistoryDateFilter();
    }
    this.feeHistoryLoading = true;
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-booking-fees?customerId=${this.memberId}`,
      )
      .subscribe(
        (rows: TrainerBookingFeeRow[]) => {
          this.feeHistoryLoading = false;
          this.feeHistoryAllRows = (Array.isArray(rows) ? rows : []).map(
            (row) => ({
              ...row,
              _id: String(row._id),
              balance: Number(row.balance) || 0,
              parentFeeId: row.parentFeeId
                ? String(row.parentFeeId)
                : undefined,
            }),
          );
          this.rebuildFeeById();
          this.feeHistoryFirst = 0;
        },
        (err: any) => {
          this.feeHistoryLoading = false;
          this.feeHistoryAllRows = [];
          this.feeHistoryFirst = 0;
          this.toastError(err, this.setLabel('loadFeesFailedLabel'));
        },
      );
  }

  /** Default range: current calendar month (1st → last day). */
  private resetFeeHistoryDateFilter(): void {
    const now = new Date();
    this.feeHistoryFrom = new Date(now.getFullYear(), now.getMonth(), 1);
    this.feeHistoryTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.feeHistoryFirst = 0;
  }

  get filteredFeeHistoryRows(): TrainerBookingFeeRow[] {
    const rows = this.feeHistoryAllRows;
    if (!this.feeHistoryFrom && !this.feeHistoryTo) {
      return rows;
    }
    return rows.filter((fee) => this.feeMatchesDateRange(fee));
  }

  get showFeeTaxColumn(): boolean {
    return (
      !this.hideTaxInfo ||
      this.filteredFeeHistoryRows.some((f) => Number(f?.tax) > 0)
    );
  }

  get pagedFeeHistoryRows(): TrainerBookingFeeRow[] {
    const rows = this.filteredFeeHistoryRows;
    return rows.slice(
      this.feeHistoryFirst,
      this.feeHistoryFirst + this.feeHistoryPageSize,
    );
  }

  get feeHistoryPageInfo(): string {
    const total = this.filteredFeeHistoryRows.length;
    if (!total) {
      return '';
    }
    const from = this.feeHistoryFirst + 1;
    const to = Math.min(
      this.feeHistoryFirst + this.feeHistoryPageSize,
      total,
    );
    return `${from}–${to} / ${total}`;
  }

  get canPrevFeeHistoryPage(): boolean {
    return this.feeHistoryFirst > 0;
  }

  get canNextFeeHistoryPage(): boolean {
    return (
      this.feeHistoryFirst + this.feeHistoryPageSize <
      this.filteredFeeHistoryRows.length
    );
  }

  onFeeHistoryDateFilterChange(): void {
    this.feeHistoryFirst = 0;
  }

  prevFeeHistoryPage(): void {
    if (!this.canPrevFeeHistoryPage) {
      return;
    }
    this.feeHistoryFirst = Math.max(
      0,
      this.feeHistoryFirst - this.feeHistoryPageSize,
    );
  }

  nextFeeHistoryPage(): void {
    if (!this.canNextFeeHistoryPage) {
      return;
    }
    this.feeHistoryFirst += this.feeHistoryPageSize;
  }

  private feeMatchesDateRange(fee: TrainerBookingFeeRow): boolean {
    const created = this.feeCreatedDate(fee);
    if (!created) {
      return false;
    }
    if (this.feeHistoryFrom) {
      const start = new Date(
        this.feeHistoryFrom.getFullYear(),
        this.feeHistoryFrom.getMonth(),
        this.feeHistoryFrom.getDate(),
        0,
        0,
        0,
        0,
      );
      if (created < start) {
        return false;
      }
    }
    if (this.feeHistoryTo) {
      const end = new Date(
        this.feeHistoryTo.getFullYear(),
        this.feeHistoryTo.getMonth(),
        this.feeHistoryTo.getDate(),
        23,
        59,
        59,
        999,
      );
      if (created > end) {
        return false;
      }
    }
    return true;
  }

  private feeCreatedDate(fee: TrainerBookingFeeRow): Date | null {
    const raw = fee?.createdAt as unknown;
    if (!raw) {
      return null;
    }
    const d = raw instanceof Date ? raw : new Date(raw as string);
    if (Number.isNaN(d.getTime())) {
      return null;
    }
    return d;
  }

  feePackageName(fee: TrainerBookingFeeRow): string {
    const pkg =
      this.packageById.get(this.refId(fee.packageId)) ||
      this.allPackageById.get(this.refId(fee.packageId));
    return pkg?.name || this.setLabel('sessionFallbackTitleLabel');
  }

  feeKind(fee: TrainerBookingFeeRow): 'one_to_one' | 'class' | 'series' {
    if (fee?.seriesId) {
      return 'series';
    }
    const pkg =
      this.packageById.get(this.refId(fee.packageId)) ||
      this.allPackageById.get(this.refId(fee.packageId));
    if (pkg?.classKind === 'program') {
      return 'series';
    }
    return pkg?.type === 'one_to_one' ? 'one_to_one' : 'class';
  }

  feeKindLabel(fee: TrainerBookingFeeRow): string {
    const kind = this.feeKind(fee);
    if (kind === 'one_to_one') {
      return this.setLabel('oneToOneSessionLabel');
    }
    if (kind === 'series') {
      return this.setLabel('seriesGroupBadgeLabel');
    }
    return this.setLabel('classGroupLabel');
  }

  feeDateLabel(fee: TrainerBookingFeeRow): string {
    if (!fee?.createdAt) {
      return '—';
    }
    const d = new Date(fee.createdAt);
    if (Number.isNaN(d.getTime())) {
      return '—';
    }
    // 12-hour invoice date/time (Fee Details style)
    return d.toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  openFeeCommentDialog(fee: TrainerBookingFeeRow): void {
    this.commentDialogText = (fee?.description || '').trim();
    this.commentDialogVisible = true;
  }

  closeFeeCommentDialog(): void {
    this.commentDialogVisible = false;
    this.commentDialogText = '';
  }

  printFeeReceipt(
    fee: TrainerBookingFeeRow,
    allFees?: TrainerBookingFeeRow[],
  ): void {
    if (!fee || !this.singleReceiptPrint || !this.printBranch) {
      this.messageService.add({
        severity: 'error',
        summary: this.setLabel('errorSummaryLabel'),
        detail: this.setLabel('printFailedLabel'),
      });
      return;
    }
    const pool = allFees?.length ? allFees : this.feeHistoryAllRows;
    const prepared = prepareFeeInvoiceForPrint(fee, pool);
    const printFee = new IFeeDetailPrint();
    printFee.invoice = prepared.invoice || fee.invoice || '—';
    printFee.createdDate = prepared.createdAt
      ? new Date(prepared.createdAt)
      : fee.createdAt
        ? new Date(fee.createdAt)
        : new Date();
    printFee.membershipNumber = this.memberTag || '—';
    printFee.memberName = this.memberName || '—';
    printFee.feeType = 'Trainer Booking Fee';
    printFee.showTrainerBookingDetails = true;
    printFee.packageType = this.feeKindLabel(fee);
    printFee.packageName = this.feePackageName(fee);
    printFee.trainerName = this.trainerName(fee.trainerId) || '—';
    printFee.fee = String(prepared.pay ?? prepared.baseAmount ?? fee.baseAmount ?? 0);
    printFee.discount = String(prepared.discount ?? fee.discount ?? 0);
    printFee.tax = String(prepared.tax ?? fee.tax ?? 0);
    printFee.taxRate = Number(prepared.taxRate ?? fee.taxRate) || 0;
    printFee.taxCalculationType =
      prepared.taxCalculationType || fee.taxCalculationType || 'NO_TAX';
    printFee.paymentMode = prepared.paymentMode || fee.paymentMode || '—';
    const balance = Number(prepared.balance ?? fee.balance) || 0;
    if (balance > 0) {
      printFee.balance = balance;
    }

    // Same as add-fee / subscription: show SRB id(s) when present.
    const group = getSplitGroupFees(pool, fee);
    const srbLines =
      Array.isArray(prepared.SRBInvoiceLines) && prepared.SRBInvoiceLines.length
        ? prepared.SRBInvoiceLines
        : buildSplitSrbInvoiceLines(group.length > 1 ? group : [fee]);
    if (srbLines.length > 1) {
      printFee.SRBInvoiceIDs = srbLines;
    } else if (srbLines.length === 1) {
      printFee.SRBInvoiceID =
        prepared.SRBInvoiceID || fee.SRBInvoiceID || srbLines[0];
    } else if (prepared.SRBInvoiceID || fee.SRBInvoiceID) {
      printFee.SRBInvoiceID = prepared.SRBInvoiceID || fee.SRBInvoiceID;
    }

    const fbrLines =
      Array.isArray(prepared.FBRInvoiceLines) && prepared.FBRInvoiceLines.length
        ? prepared.FBRInvoiceLines
        : buildSplitFbrInvoiceLines(group.length > 1 ? group : [fee]);
    if (fbrLines.length > 1) {
      printFee.FBRInvoiceIDs = fbrLines;
    } else if (fbrLines.length === 1) {
      printFee.FBRInvoiceID =
        prepared.FBRInvoiceID || fee.FBRInvoiceID || fbrLines[0];
    } else if (prepared.FBRInvoiceID || fee.FBRInvoiceID) {
      printFee.FBRInvoiceID = prepared.FBRInvoiceID || fee.FBRInvoiceID;
    }

    this.singleReceiptPrint.initiatePrintInvoice(printFee);
  }

  primaryFeeRecord(fee: TrainerBookingFeeRow): TrainerBookingFeeRow {
    if (!fee?.parentFeeId) {
      return fee;
    }
    const parentId = this.refId(fee.parentFeeId);
    return this.feeById.get(parentId) || fee;
  }

  feeBalance(fee: TrainerBookingFeeRow): number {
    return Number(this.primaryFeeRecord(fee).balance) || 0;
  }

  hasFeeBalance(fee: TrainerBookingFeeRow): boolean {
    return !fee?.parentFeeId && this.feeBalance(fee) > 0;
  }

  isFeeChildRow(fee: TrainerBookingFeeRow): boolean {
    return !!fee?.parentFeeId;
  }

  splitGroupFees(fee: TrainerBookingFeeRow): TrainerBookingFeeRow[] {
    return getSplitGroupFees(
      this.feeHistoryAllRows,
      fee,
    ) as TrainerBookingFeeRow[];
  }

  feeSplitPartLabel(fee: TrainerBookingFeeRow): string | null {
    return getSplitPartLabel(
      fee,
      this.feeHistoryAllRows,
      this.setLabel('splitPartLabel') || 'Split {index}/{total}',
    );
  }

  feeForBooking(b: TrainerBookingRow): TrainerBookingFeeRow | null {
    if (!b?.feeId) {
      return null;
    }
    const hit = this.feeById.get(String(b.feeId));
    return hit ? this.primaryFeeRecord(hit) : null;
  }

  bookingHasFeeBalance(b: TrainerBookingRow): boolean {
    const fee = this.feeForBooking(b);
    return !!fee && this.hasFeeBalance(fee);
  }

  groupHasFeeBalance(g: SeriesEnrollmentGroup): boolean {
    const feeId = g.feeId ? String(g.feeId) : '';
    if (!feeId) {
      return false;
    }
    const fee = this.feeById.get(feeId);
    return !!fee && this.hasFeeBalance(fee);
  }

  openPayRemainingForBooking(b: TrainerBookingRow): void {
    const fee = this.feeForBooking(b);
    if (fee) {
      this.openPayRemainingDialog(fee);
    }
  }

  openPayRemainingForGroup(g: SeriesEnrollmentGroup): void {
    if (!g.feeId) {
      return;
    }
    const fee = this.feeById.get(String(g.feeId));
    if (fee) {
      this.openPayRemainingDialog(fee);
    }
  }

  private normalizeFeesResponse(res: any): TrainerBookingFeeRow[] {
    const raw = Array.isArray(res?.fees)
      ? res.fees
      : res?.fee
        ? [res.fee]
        : [];
    return raw.map((feeDoc: any) => this.mapApiFee(feeDoc));
  }

  private mapApiFee(feeDoc: any): TrainerBookingFeeRow {
    return {
      _id: String(feeDoc._id || ''),
      bookingId: feeDoc.bookingId ? String(feeDoc.bookingId) : '',
      sessionId: feeDoc.sessionId ? String(feeDoc.sessionId) : '',
      packageId: String(feeDoc.packageId || ''),
      seriesId: feeDoc.seriesId ? String(feeDoc.seriesId) : undefined,
      trainerId: String(feeDoc.trainerId || ''),
      customerId: String(feeDoc.customerId || this.memberId || ''),
      baseAmount: Number(feeDoc.baseAmount ?? feeDoc.pay) || 0,
      discount: Number(feeDoc.discount) || 0,
      tax: Number(feeDoc.tax) || 0,
      taxRate: Number(feeDoc.taxRate) || 0,
      taxCalculationType: feeDoc.taxCalculationType || 'NO_TAX',
      totalPaid: Number(feeDoc.totalPaid) || 0,
      paymentMode: feeDoc.paymentMode || '',
      invoice: feeDoc.invoice,
      description: feeDoc.description,
      feeReceiptImageUrl: feeDoc.feeReceiptImageUrl,
      createdAt: feeDoc.createdAt,
      balance: Number(feeDoc.balance) || 0,
      parentFeeId: feeDoc.parentFeeId
        ? String(feeDoc.parentFeeId)
        : undefined,
      splitGroupId: feeDoc.splitGroupId
        ? String(feeDoc.splitGroupId)
        : undefined,
      splitIndex: feeDoc.splitIndex != null ? Number(feeDoc.splitIndex) : undefined,
      splitTotal: feeDoc.splitTotal != null ? Number(feeDoc.splitTotal) : undefined,
      isSplitPayment: !!feeDoc.isSplitPayment,
      SRBInvoiceID: feeDoc.SRBInvoiceID
        ? String(feeDoc.SRBInvoiceID)
        : undefined,
      FBRInvoiceID: feeDoc.FBRInvoiceID
        ? String(feeDoc.FBRInvoiceID)
        : undefined,
    };
  }

  private rebuildFeeById(): void {
    this.feeById.clear();
    for (const row of this.feeHistoryAllRows) {
      this.feeById.set(row._id, row);
    }
  }

  deleteFeeRecord(fee: TrainerBookingFeeRow): void {
    if (
      !fee?._id ||
      this.deletingFeeId ||
      !this.canDeleteTrainerFee() ||
      this.isFeeChildRow(fee)
    ) {
      return;
    }
    this.confirmationService.confirm({
      message: this.setLabel(
        fee.bookingId ? 'deleteFeeConfirmLabel' : 'deleteSharedFeeConfirmLabel',
      ),
      header: this.setLabel('deleteFeeConfirmHeaderLabel'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.doDeleteFee(fee),
    });
  }

  canDeleteTrainerFee(): boolean {
    const user = AuthService.getLoggedInUser();
    const name = String(user?.name || user?.username || '')
      .trim()
      .toLowerCase();
    if (name === 'tecgym admin') {
      return true;
    }
    const roleName = String(AuthService.getLoggedInRole()?.name || '').trim();
    if (!roleName || !this.feeDeleteRoles.length) {
      return false;
    }
    return this.feeDeleteRoles.includes(roleName);
  }

  private doDeleteFee(fee: TrainerBookingFeeRow): void {
    this.deletingFeeId = fee._id;
    this.appServiceManager
      .delete(`branches/${this.branchId}/trainer-booking-fees/${fee._id}`)
      .subscribe(
        () => {
          this.deletingFeeId = null;
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('deleteFeeSuccessLabel'),
          });
          this.loadFeeHistory();
          this.refreshAfterMutation(fee.trainerId);
        },
        (err: any) => {
          this.deletingFeeId = null;
          this.toastError(err, this.setLabel('deleteFeeFailedLabel'));
        },
      );
  }

  private uploadReceiptThen(done: () => void): void {
    if (!this.selectedReceiptFile || !this.branchId) {
      done();
      return;
    }
    const formData = new FormData();
    formData.append(
      'feeReceiptImage',
      this.selectedReceiptFile,
      this.selectedReceiptFile.name,
    );
    this.receiptUploading = true;
    this.appServiceManager
      .post(
        `branches/${this.branchId}/trainer-booking-fees/upload-receipt`,
        formData,
      )
      .subscribe(
        (res: any) => {
          this.receiptUploading = false;
          if (res?.status && res?.data?.feeReceiptImageUrl) {
            this.feeReceiptImageUrl = res.data.feeReceiptImageUrl;
            done();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.setLabel('errorSummaryLabel'),
              detail: this.setLabel('receiptUploadFailedLabel'),
            });
          }
        },
        (err: any) => {
          this.receiptUploading = false;
          this.toastError(err, this.setLabel('receiptUploadFailedLabel'));
        },
      );
  }

  cancelBooking(b: TrainerBookingRow): void {
    if (!b?._id || b.status !== 'confirmed' || this.cancellingId) {
      return;
    }
    this.confirmationService.confirm({
      message: this.setLabel('cancelConfirmLabel'),
      header: this.setLabel('cancelConfirmHeaderLabel'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.doCancel(b._id, b.trainerId),
    });
  }

  private doBook(
    sessionId: string,
    packageId?: string,
    trainerId?: string,
  ): void {
    this.bookingBusy = true;
    const body: Record<string, unknown> = {
      sessionId,
      customerId: this.memberId,
      discount: Number(this.bookDiscount) || 0,
    };
    this.appendCollectFields(body);
    if (packageId) {
      body.packageId = packageId;
    }
    if (this.feeReceiptImageUrl) {
      body.feeReceiptImageUrl = this.feeReceiptImageUrl;
    }
    if (this.bookComment?.trim()) {
      body.description = this.bookComment.trim();
    }
    this.appServiceManager
      .post(`branches/${this.branchId}/trainer-bookings`, body)
      .subscribe(
        (res: any) => {
          this.bookingBusy = false;
          const fees = this.normalizeFeesResponse(res);
          this.closeBookDialog();
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('bookSuccessLabel'),
          });
          this.refreshAfterMutation(trainerId);
          if (fees.length) {
            const printFee = fees.find((f) => !f.parentFeeId) || fees[0];
            setTimeout(() => this.printFeeReceipt(printFee, fees), 350);
          }
        },
        (err: any) => {
          this.bookingBusy = false;
          this.toastError(err, this.setLabel('bookFailedLabel'));
        },
      );
  }

  private doBookMany(
    sessionIds: string[],
    packageId?: string,
    trainerId?: string,
  ): void {
    this.bookingBusy = true;
    const body: Record<string, unknown> = {
      sessionIds,
      customerId: this.memberId,
      discount: Number(this.bookDiscount) || 0,
    };
    this.appendCollectFields(body);
    if (packageId) {
      body.packageId = packageId;
    }
    if (this.feeReceiptImageUrl) {
      body.feeReceiptImageUrl = this.feeReceiptImageUrl;
    }
    if (this.bookComment?.trim()) {
      body.description = this.bookComment.trim();
    }
    this.appServiceManager
      .post(`branches/${this.branchId}/trainer-bookings/bulk`, body)
      .subscribe(
        (res: any) => {
          this.bookingBusy = false;
          const fees = this.normalizeFeesResponse(res);
          const sessionCount = Number(res?.sessionCount) || sessionIds.length;
          this.closeBookDialog();
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('bookManySuccessLabel').replace(
              '{count}',
              String(sessionCount),
            ),
          });
          this.refreshAfterMutation(trainerId);
          if (fees.length) {
            const printFee = fees.find((f) => !f.parentFeeId) || fees[0];
            setTimeout(() => this.printFeeReceipt(printFee, fees), 350);
          }
        },
        (err: any) => {
          this.bookingBusy = false;
          this.toastError(err, this.setLabel('bookFailedLabel'));
        },
      );
  }

  private doCancel(bookingId: string, trainerId?: string): void {
    this.cancellingId = bookingId;
    this.appServiceManager
      .patch(
        `branches/${this.branchId}/trainer-bookings/${bookingId}/cancel`,
        {},
      )
      .subscribe(
        () => {
          this.cancellingId = null;
          this.messageService.add({
            severity: 'success',
            summary: this.setLabel('successSummaryLabel'),
            detail: this.setLabel('cancelSuccessLabel'),
          });
          if (this.detailBooking && this.detailBooking._id === bookingId) {
            this.detailBooking = null;
          }
          this.refreshAfterMutation(trainerId);
        },
        (err: any) => {
          this.cancellingId = null;
          this.toastError(err, this.setLabel('cancelFailedLabel'));
        },
      );
  }

  private refreshAfterMutation(trainerId?: string): void {
    if (trainerId) {
      this.sessionsByTrainerId.delete(String(trainerId));
    } else {
      this.sessionsByTrainerId.clear();
      this.sessionsCacheRange = '';
    }
    this.ensureSessionsForChecked().then(() => {
      this.loadMemberBookingsOnce();
    });
    if (this.activeTabIndex === 1) {
      this.loadAllMemberBookings();
    } else if (this.activeTabIndex === 2) {
      this.loadFeeHistory();
    }
  }

  private initCalendar(): void {
    this.calendarOptions = {
      plugins: [dayGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      height: 'auto',
      fixedWeekCount: false,
      showNonCurrentDates: true,
      displayEventTime: false,
      dayMaxEvents: 3,
      headerToolbar: false,
      datesSet: (arg: any) => this.onDatesSet(arg),
      dateClick: (arg: any) => this.openDay(this.toLocalDateStr(arg.date)),
      eventClick: (arg: any) => {
        const localDate =
          arg.event?.extendedProps?.localDate ||
          (arg.event?.start ? this.toLocalDateStr(arg.event.start) : null);
        if (localDate) {
          this.openDay(localDate);
        }
      },
      eventContent: (arg: any) => this.dayEventContent(arg),
    };
  }

  /** Navy chip: type ticks + slot count + trainer names. */
  private dayEventContent(arg: any): { domNodes: Node[] } {
    const count = Number(arg.event?.extendedProps?.availableCount || 0);
    const names: string[] = Array.isArray(arg.event?.extendedProps?.trainerNames)
      ? arg.event.extendedProps.trainerNames
      : [];
    const tones: Array<'one' | 'class' | 'series'> = Array.isArray(
      arg.event?.extendedProps?.tones,
    )
      ? arg.event.extendedProps.tones
      : [];
    const countLabel =
      count === 1
        ? `1 ${this.setLabel('sessionSingularLabel')}`
        : `${count} ${this.setLabel('sessionPluralLabel')}`;

    const wrap = document.createElement('div');
    wrap.className = 'bt-day-chip';

    if (tones.length) {
      const ticks = document.createElement('div');
      ticks.className = 'bt-day-chip-ticks';
      for (const tone of tones) {
        const tick = document.createElement('span');
        tick.className = `bt-day-chip-tick bt-day-chip-tick--${tone}`;
        ticks.appendChild(tick);
      }
      wrap.appendChild(ticks);
    }

    const countEl = document.createElement('div');
    countEl.className = 'bt-day-chip-count';
    countEl.textContent = countLabel;
    wrap.appendChild(countEl);

    if (names.length) {
      const namesEl = document.createElement('div');
      namesEl.className = 'bt-day-chip-names';
      namesEl.textContent = names.join(', ');
      namesEl.title = names.join(', ');
      wrap.appendChild(namesEl);
    }

    return { domNodes: [wrap] };
  }

  private onDatesSet(arg: any): void {
    const title = arg?.view?.title || '';
    // FullCalendar datesSet runs during initial CD — defer title bind (NG0100).
    queueMicrotask(() => {
      this.calendarTitle = title;
      this.cdr.markForCheck();
    });
    this.rangeFrom = this.toLocalDateStr(arg.start);
    const end = new Date(arg.end.getTime() - 24 * 60 * 60 * 1000);
    this.rangeTo = this.toLocalDateStr(end);
    const rangeKey = `${this.rangeFrom}|${this.rangeTo}`;
    if (this.sessionsCacheRange !== rangeKey) {
      this.sessionsByTrainerId.clear();
      this.sessionsCacheRange = rangeKey;
    }
    if (this.branchId && this.checkedTrainerIds.size) {
      this.ensureSessionsForChecked();
    } else {
      this.applyAvailabilityFromCache();
    }
  }

  private openDay(localDate: string): void {
    this.selectedLocalDate = localDate;
    this.rebuildDayPanelLists();
  }

  private resetFilterState(): void {
    this.filterOneToOne = false;
    this.filterClass = false;
    this.filterSeries = false;
    this.selectedServiceIds = new Set();
    this.checkedTrainerIds = new Set();
    this.packagesByTrainerId.clear();
    this.sessionsByTrainerId.clear();
    this.sessionsCacheRange = '';
    this.packageById.clear();
    this.sessions = [];
    this.availableSessions = [];
    this.daySeriesCardsList = [];
    this.daySingleSessionsList = [];
    this.memberBookings = [];
    this.sessionById.clear();
    this.events = [];
    this.selectedLocalDate = null;
    this.clearFilterDebounce();
    this.endCalendarWork();
  }

  private loadTrainersAndPackages(): void {
    if (!this.branchId) {
      this.bootstrapLoading = false;
      return;
    }
    this.loadTrainingServices();
    this.appServiceManager
      .get(`branches/${this.branchId}/users/actions/trainers`)
      .subscribe(
        (res: any[]) => {
          const rows = (Array.isArray(res) ? res : []).filter(
            (t) => t && t.isDelete === false,
          );
          rows.sort((a, b) =>
            String(a.name || '').localeCompare(String(b.name || '')),
          );
          this.trainerOptions = rows.map((t, i) => ({
            label: t.name,
            value: String(t._id),
            color: TRAINER_COLORS[i % TRAINER_COLORS.length],
          }));
          this.loadPackageIndex();
        },
        (err: any) => {
          this.bootstrapLoading = false;
          this.toastError(err, this.setLabel('loadTrainersFailedLabel'));
        },
      );
  }

  private loadPackageIndex(): void {
    if (!this.branchId || !this.trainerOptions.length) {
      this.packagesByTrainerId.clear();
      this.rebuildPackageById();
      this.bootstrapLoading = false;
      return;
    }
    const calls = this.trainerOptions.map((t) =>
      this.appServiceManager
        .get(`branches/${this.branchId}/trainer-packages?trainerId=${t.value}`)
        .pipe(
          catchError(() => of([] as TrainerPackageOption[])),
          map((pkgs: TrainerPackageOption[]) => ({
            trainerId: t.value,
            packages: (Array.isArray(pkgs) ? pkgs : []).map((p) => ({
              ...p,
              _id: this.refId(p._id),
              trainerId: t.value,
              price: Number((p as TrainerPackageOption).price) || 0,
              serviceIds: this.packageServiceIds(p),
            })),
          })),
        ),
    );
    forkJoin(calls).subscribe(
      (rows: { trainerId: string; packages: TrainerPackageOption[] }[]) => {
        this.packagesByTrainerId.clear();
        for (const row of rows) {
          this.packagesByTrainerId.set(row.trainerId, row.packages);
        }
        this.rebuildPackageById();
        this.pruneCheckedTrainers();
        this.applyAvailabilityFromCache();
        this.bootstrapLoading = false;
      },
      () => {
        this.packagesByTrainerId.clear();
        this.rebuildPackageById();
        this.bootstrapLoading = false;
      },
    );
  }

  private rebuildPackageById(): void {
    this.packageById.clear();
    this.packagesByTrainerId.forEach((pkgs) => {
      pkgs.forEach((p) => {
        const id = this.refId(p._id);
        if (id) {
          this.packageById.set(id, {
            ...p,
            classKind:
              p.type === 'class'
                ? p.classKind === 'program' ||
                  (p.classKind as string) === 'series'
                  ? 'program'
                  : 'single'
                : undefined,
            monthWise:
              p.type === 'class' &&
              (p.classKind === 'program' ||
                (p.classKind as string) === 'series') &&
              p.monthWise === true,
          });
        }
      });
    });
  }

  private trainerMatchesTypeFilter(trainerId: string): boolean {
    if (!this.selectedServiceIds.size) {
      return false;
    }
    const pkgs = this.trainerPackages(trainerId);
    return pkgs.some((p) => {
      if (p.isActive === false) {
        return false;
      }
      if (!this.packageOverlapsSelectedServices(p)) {
        return false;
      }
      if (p.type === 'one_to_one' && this.filterOneToOne) {
        return true;
      }
      if (p.type === 'class' && p.classKind === 'program' && this.filterSeries) {
        return true;
      }
      if (p.type === 'class' && p.classKind !== 'program' && this.filterClass) {
        return true;
      }
      return false;
    });
  }

  private onServiceFilterChange(): void {
    this.pruneCheckedTrainers();
    this.selectedLocalDate = null;
    this.scheduleCalendarRefresh();
  }

  private onTypeFilterChange(): void {
    this.pruneCheckedTrainers();
    this.selectedLocalDate = null;
    this.scheduleCalendarRefresh();
  }

  /** Debounce + deferred rebuild for filter toggles (keeps click UI snappy). */
  private scheduleCalendarRefresh(): void {
    this.clearFilterDebounce();
    this.filterDebounceTimer = setTimeout(() => {
      this.filterDebounceTimer = null;
      this.runCalendarRefresh();
    }, BookTrainerComponent.FILTER_DEBOUNCE_MS);
  }

  private runCalendarRefresh(): void {
    const gen = ++this.calendarWorkGen;
    this.beginCalendarWork();
    requestAnimationFrame(() => {
      if (gen !== this.calendarWorkGen) {
        return;
      }
      this.applyAvailabilityFromCache();
      this.endCalendarWork();
      this.cdr.markForCheck();
    });
  }

  private beginCalendarWork(): void {
    this.clearCalendarBusyTimer();
    this.calendarBusyTimer = setTimeout(() => {
      this.calendarBusyTimer = null;
      this.calendarBusy = true;
      this.cdr.markForCheck();
    }, BookTrainerComponent.CALENDAR_BUSY_DELAY_MS);
  }

  private endCalendarWork(): void {
    this.clearCalendarBusyTimer();
    if (this.calendarBusy) {
      this.calendarBusy = false;
    }
  }

  private clearCalendarBusyTimer(): void {
    if (this.calendarBusyTimer != null) {
      clearTimeout(this.calendarBusyTimer);
      this.calendarBusyTimer = null;
    }
  }

  private clearFilterDebounce(): void {
    if (this.filterDebounceTimer != null) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
  }

  private pruneCheckedTrainers(): void {
    const visible = new Set(this.visibleTrainers.map((t) => t.value));
    let changed = false;
    Array.from(this.checkedTrainerIds).forEach((id) => {
      if (!visible.has(id)) {
        this.checkedTrainerIds.delete(id);
        changed = true;
      }
    });
    if (changed) {
      this.checkedTrainerIds = new Set(this.checkedTrainerIds);
    }
  }

  private ensureSessionsForChecked(): Promise<void> {
    if (
      !this.branchId ||
      !this.rangeFrom ||
      !this.rangeTo ||
      !this.checkedTrainerIds.size
    ) {
      this.runCalendarRefresh();
      return Promise.resolve();
    }
    const missing = Array.from(this.checkedTrainerIds).filter(
      (id) => !this.sessionsByTrainerId.has(id),
    );
    if (!missing.length) {
      this.runCalendarRefresh();
      return Promise.resolve();
    }
    const gen = ++this.calendarWorkGen;
    this.beginCalendarWork();
    const calls = missing.map((trainerId) =>
      this.appServiceManager
        .get(
          `branches/${this.branchId}/trainer-sessions?trainerId=${trainerId}&fromLocalDate=${this.rangeFrom}&toLocalDate=${this.rangeTo}&status=scheduled`,
        )
        .pipe(
          catchError(() => of([] as TrainerSessionRow[])),
          map((sessions: TrainerSessionRow[]) => ({
            trainerId,
            sessions: (Array.isArray(sessions) ? sessions : []).map((s) => ({
              ...s,
              _id: this.refId(s._id),
              trainerId,
              packageId: this.refId(s.packageId) || undefined,
              seriesId: this.refId((s as TrainerSessionRow).seriesId) || undefined,
              seriesSummary: (s as TrainerSessionRow).seriesSummary,
              type: s.type,
            })),
          })),
        ),
    );
    return new Promise((resolve) => {
      forkJoin(calls).subscribe(
        (rows: { trainerId: string; sessions: TrainerSessionRow[] }[]) => {
          if (gen !== this.calendarWorkGen) {
            resolve();
            return;
          }
          for (const row of rows) {
            this.sessionsByTrainerId.set(row.trainerId, row.sessions);
          }
          this.sessionsCacheRange = `${this.rangeFrom}|${this.rangeTo}`;
          this.applyAvailabilityFromCache();
          this.endCalendarWork();
          this.cdr.markForCheck();
          resolve();
        },
        () => {
          if (gen === this.calendarWorkGen) {
            this.applyAvailabilityFromCache();
            this.endCalendarWork();
            this.cdr.markForCheck();
          }
          resolve();
        },
      );
    });
  }

  private loadMemberBookingsOnce(): void {
    if (!this.memberId || !this.branchId) {
      return;
    }
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-bookings?customerId=${this.memberId}`,
      )
      .subscribe(
        (res: TrainerBookingRow[]) => {
          this.memberBookings = (Array.isArray(res) ? res : []).map((b) => ({
            ...b,
            seriesId: b.seriesId ? String(b.seriesId) : undefined,
          }));
          this.loadEnrolledSeriesIds();
          this.applyAvailabilityFromCache();
        },
        (err: any) => {
          this.memberBookings = [];
          this.loadEnrolledSeriesIds();
          this.toastError(err, this.setLabel('loadBookingsFailedLabel'));
          this.applyAvailabilityFromCache();
        },
      );
  }

  private applyAvailabilityFromCache(): void {
    this.bookedSessionIds = new Set(
      this.memberBookings
        .filter((b) => b.status === 'confirmed')
        .map((b) => String(b.sessionId)),
    );

    const flat: TrainerSessionRow[] = [];
    this.checkedTrainerIds.forEach((id) => {
      const rows = this.sessionsByTrainerId.get(id) || [];
      rows.forEach((s) => flat.push(s));
    });

    this.sessions = flat;
    this.sessionById.clear();
    flat.forEach((s) => this.sessionById.set(s._id, s));

    this.availableSessions = flat.filter((s) => {
      if (s.status !== 'scheduled') {
        return false;
      }
      if (s.bookedCount >= s.maxMembers) {
        return false;
      }
      if (this.bookedSessionIds.has(String(s._id))) {
        return false;
      }
      if (s.seriesId && this.fullyEnrolledSeriesIds.has(String(s.seriesId))) {
        return false;
      }
      const tone = this.sessionCalendarTone(s);
      if (tone === 'one' && !this.filterOneToOne) {
        return false;
      }
      if (tone === 'class' && !this.filterClass) {
        return false;
      }
      if (tone === 'series' && !this.filterSeries) {
        return false;
      }
      if (!this.sessionMatchesServiceFilter(s)) {
        return false;
      }
      return true;
    });
    this.rebuildEvents();
    this.rebuildDayPanelLists();
  }

  /** Class: package serviceIds overlap; 1:1: trainer has active 1:1 pkg overlapping. */
  private sessionMatchesServiceFilter(s: TrainerSessionRow): boolean {
    if (!this.selectedServiceIds.size) {
      return false;
    }
    if (this.isOneToOneSession(s)) {
      return this.trainerOneToOnePackages(s.trainerId).length > 0;
    }
    const pkg = this.findPackageForSession(s);
    return pkg ? this.packageOverlapsSelectedServices(pkg) : false;
  }

  private packageServiceIds(pkg: TrainerPackageOption | Record<string, unknown>): string[] {
    const raw = (pkg as TrainerPackageOption).serviceIds;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((id) => this.refId(id)).filter(Boolean);
  }

  private packageOverlapsSelectedServices(pkg: TrainerPackageOption): boolean {
    if (!this.selectedServiceIds.size) {
      return false;
    }
    const ids = this.packageServiceIds(pkg);
    return ids.some((id) => this.selectedServiceIds.has(id));
  }

  private loadTrainingServices(): void {
    if (!this.branchId) {
      this.trainingServiceOptions = [];
      return;
    }
    this.appServiceManager.get(`branches/${this.branchId}/services`).subscribe(
      (res: { response?: Array<{ _id: string; name?: string; type?: string; isDelete?: boolean }> }) => {
        const rows = Array.isArray(res?.response) ? res.response : [];
        const training = rows.filter(
          (s) => s && s.type === 'trainingService' && s.isDelete !== true,
        );
        training.sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || '')),
        );
        this.trainingServiceOptions = training.map((s) => ({
          label: String(s.name || s._id),
          value: String(s._id),
        }));
      },
      () => {
        this.trainingServiceOptions = [];
      },
    );
  }

  private findCachedSession(sessionId: string): TrainerSessionRow | undefined {
    for (const rows of this.sessionsByTrainerId.values()) {
      const hit = rows.find((s) => s._id === sessionId);
      if (hit) {
        return hit;
      }
    }
    return undefined;
  }

  private loadAllMemberBookings(): void {
    if (!this.memberId || !this.branchId) {
      return;
    }
    this.allBookingsLoading = true;
    this.detailBooking = null;
    this.detailSeriesGroup = null;
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-bookings?customerId=${this.memberId}`,
      )
      .subscribe(
        (res: TrainerBookingRow[]) => {
          this.allMemberBookings = (Array.isArray(res) ? res : []).map((b) => ({
            ...b,
            seriesId: b.seriesId ? String(b.seriesId) : undefined,
            feeId: b.feeId ? String(b.feeId) : undefined,
          }));
          this.loadFeesForAllBookings();
          this.enrichAllBookings();
        },
        (err: any) => {
          this.allMemberBookings = [];
          this.allSessionById.clear();
          this.allPackageById.clear();
          this.seriesGroups = [];
          this.sharedFeeGroups = [];
          this.sharedFeeBookingIdSet = new Set();
          this.allBookingsLoading = false;
          this.toastError(err, this.setLabel('loadBookingsFailedLabel'));
        },
      );
  }

  private loadFeesForAllBookings(): void {
    if (!this.memberId || !this.branchId) {
      return;
    }
    this.appServiceManager
      .get(
        `branches/${this.branchId}/trainer-booking-fees?customerId=${this.memberId}`,
      )
      .subscribe(
        (rows: TrainerBookingFeeRow[]) => {
          this.feeHistoryAllRows = (Array.isArray(rows) ? rows : []).map(
            (row) => ({
              ...row,
              _id: String(row._id),
              balance: Number(row.balance) || 0,
              parentFeeId: row.parentFeeId
                ? String(row.parentFeeId)
                : undefined,
            }),
          );
          this.rebuildFeeById();
        },
        () => {
          /* ponytail: all-bookings badges degrade without fee map */
        },
      );
  }

  private enrichAllBookings(): void {
    const trainerIds = Array.from(
      new Set(
        this.allMemberBookings
          .map((b) => String(b.trainerId || ''))
          .filter(Boolean),
      ),
    );
    if (!trainerIds.length) {
      this.allSessionById.clear();
      this.allPackageById.clear();
      this.rebuildSeriesGroups();
      this.allBookingsLoading = false;
      return;
    }

    const from = this.shiftLocalDate(new Date(), -400);
    const to = this.shiftLocalDate(new Date(), 200);
    const calls = trainerIds.map((trainerId) =>
      forkJoin({
        trainerId: of(trainerId),
        packages: (
          this.appServiceManager.get(
            `branches/${this.branchId}/trainer-packages?trainerId=${trainerId}`,
          ) as Observable<TrainerPackageOption[]>
        ).pipe(catchError(() => of([] as TrainerPackageOption[]))),
        sessions: forkJoin([
          this.appServiceManager
            .get(
              `branches/${this.branchId}/trainer-sessions?trainerId=${trainerId}&fromLocalDate=${from}&toLocalDate=${to}&status=scheduled`,
            )
            .pipe(catchError(() => of([] as TrainerSessionRow[]))),
          this.appServiceManager
            .get(
              `branches/${this.branchId}/trainer-sessions?trainerId=${trainerId}&fromLocalDate=${from}&toLocalDate=${to}&status=cancelled`,
            )
            .pipe(catchError(() => of([] as TrainerSessionRow[]))),
        ]).pipe(
          map((parts: any) => {
            const scheduled = Array.isArray(parts?.[0]) ? parts[0] : [];
            const cancelled = Array.isArray(parts?.[1]) ? parts[1] : [];
            return [...scheduled, ...cancelled] as TrainerSessionRow[];
          }),
        ),
      }),
    );

    forkJoin(calls).subscribe(
      (
        rows: {
          trainerId: string;
          packages: TrainerPackageOption[];
          sessions: TrainerSessionRow[];
        }[],
      ) => {
        this.allPackageById.clear();
        this.allSessionById.clear();
        for (const row of rows) {
          (Array.isArray(row.packages) ? row.packages : []).forEach((p) =>
            this.allPackageById.set(p._id, p),
          );
          (Array.isArray(row.sessions) ? row.sessions : []).forEach((s) =>
            this.allSessionById.set(s._id, s),
          );
        }
        this.allBookingsLoading = false;
        this.rebuildSeriesGroups();
      },
      () => {
        this.allBookingsLoading = false;
      },
    );
  }

  private shiftLocalDate(base: Date, dayDelta: number): string {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    d.setDate(d.getDate() + dayDelta);
    return this.toLocalDateStr(d);
  }

  private rebuildEvents(): void {
    const byDate = new Map<string, TrainerSessionRow[]>();
    for (const s of this.availableSessions) {
      const list = byDate.get(s.localDate) || [];
      list.push(s);
      byDate.set(s.localDate, list);
    }
    const next: any[] = [];
    byDate.forEach((sessions, localDate) => {
      const count = this.countBookableItemsForDay(sessions);
      if (count <= 0) {
        return;
      }
      const names = Array.from(
        new Set(
          sessions
            .map((s) => this.trainerName(s.trainerId))
            .filter((n) => !!n && n !== this.setLabel('trainerFallbackLabel')),
        ),
      );
      const countLabel =
        count === 1
          ? `1 ${this.setLabel('sessionSingularLabel')}`
          : `${count} ${this.setLabel('sessionPluralLabel')}`;
      next.push({
        id: `day-${localDate}`,
        title: countLabel,
        start: localDate,
        allDay: true,
        classNames: ['bt-day-count'],
        extendedProps: {
          localDate,
          availableCount: count,
          trainerNames: names,
          tones: this.dayChipTones(sessions),
        },
      });
    });
    this.events = next;
  }

  private sessionCalendarTone(
    s: TrainerSessionRow,
  ): 'one' | 'class' | 'series' {
    if (s.seriesId) {
      return 'series';
    }
    const pkg = this.findPackageForSession(s);
    if (pkg?.type === 'class' && pkg?.classKind === 'program') {
      return 'series';
    }
    return this.isOneToOneSession(s) ? 'one' : 'class';
  }

  private dayChipTones(
    sessions: TrainerSessionRow[],
  ): Array<'one' | 'class' | 'series'> {
    let one = false;
    let cls = false;
    let series = false;
    for (const s of sessions) {
      const tone = this.sessionCalendarTone(s);
      if (tone === 'series') {
        series = true;
      } else if (tone === 'one') {
        one = true;
      } else {
        cls = true;
      }
    }
    const tones: Array<'one' | 'class' | 'series'> = [];
    if (one) {
      tones.push('one');
    }
    if (cls) {
      tones.push('class');
    }
    if (series) {
      tones.push('series');
    }
    return tones;
  }

  private countBookableItemsForDay(sessions: TrainerSessionRow[]): number {
    const seriesSeen = new Set<string>();
    let count = 0;
    for (const s of sessions) {
      if (s.seriesId) {
        const sid = String(s.seriesId);
        if (this.fullyEnrolledSeriesIds.has(sid)) {
          continue;
        }
        if (!seriesSeen.has(sid)) {
          seriesSeen.add(sid);
          count += 1;
        }
      } else {
        count += 1;
      }
    }
    return count;
  }

  private toLocalDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    return `${this.pad(y, 4)}-${this.pad(m)}-${this.pad(day)}`;
  }

  private hhmm(total: number): string {
    const h = Math.floor(total / 60);
    const m = total % 60;
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${h12}${ampm}` : `${h12}:${this.pad(m)}${ampm}`;
  }

  private pad(n: number, width = 2): string {
    const s = String(n);
    return s.length >= width ? s : '0'.repeat(width - s.length) + s;
  }

  private toastError(err: any, fallback: string): void {
    const raw = err?.error?.message;
    const detail =
      (typeof raw === 'string' && raw) ||
      (Array.isArray(raw) ? raw.join(', ') : null) ||
      err?.error?.error ||
      fallback;
    this.messageService.add({
      severity: 'error',
      summary: this.setLabel('errorSummaryLabel'),
      detail: typeof detail === 'string' ? detail : fallback,
    });
  }
}
